import axios from "axios";

// API_URL: the backend's own "/api" origin. process.env.REACT_APP_API_URL
// (baked in at BUILD time by Create React App) is kept as an explicit
// LOCAL-DEV override only (see frontend/.env.example — a dev server on
// :3000 talking to a backend on a different origin, :8080, genuinely needs
// one). This feature's own plan doc requires the SAME static build to work
// correctly on every tenant's own subdomain without a rebuild per company:
// nginx terminates TLS for each subdomain and reverse-proxies straight to
// this one shared Go backend on that exact same origin (see deploy/
// posflect-provision-subdomain.sh's own certbot --nginx step), so falling
// back to window.location.origin — read live, in the browser, on every
// page load, never baked into the build the way an absolute env-var URL
// would be — is what makes acme.posflect.com's build call acme.posflect.com/api
// and beta.posflect.com's identical build call beta.posflect.com/api.
const API_URL = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;

// --- Protected static assets (uploaded avatars/product images, the /img
// bundled defaults) --- the backend's /uploads and /img static mounts now
// require a valid JWT (see middlewares.StaticAssetAuth in
// internal/middlewares/auth.go) so files stored there aren't reachable by
// anyone outside the system — but a plain <img>/<iframe> src can't attach
// an Authorization header the way this file's own request()/api calls do,
// so the token travels as a ?token= query param instead (same convention
// the /ws upgrade already used). Every page that used to hand-build
// "${BASE_URL}${path}" itself now calls getAssetUrl(path) instead, so the
// token doesn't have to be threaded through by hand in a dozen places.
//
// Separate from API_URL on purpose: /uploads and /img are registered
// directly on the Fiber app's bare origin, not under API_URL's own "/api"
// prefix. ASSET_BASE_URL is its own env var (REACT_APP_URL) so a
// production deployment can point it at a CDN/reverse-proxied origin
// without touching code — see frontend/.env.example — but, same reasoning
// as API_URL just above, falls back to window.location.origin (not a
// hardcoded dev host) rather than assuming one fixed backend origin bakes
// correctly into a build served on every tenant's own subdomain. A local
// dev setup still needs frontend/.env's own REACT_APP_URL (CRA's dev
// server runs on a different origin, :3000, than the Go backend, :8080) —
// this fallback only matters once REACT_APP_URL is left unset, i.e. every
// real per-subdomain production build.
const ASSET_BASE_URL = process.env.REACT_APP_URL || window.location.origin;

// getAssetUrl used to attach the caller's own long-lived (24h) session
// token as ?token=... directly — but that token then sat, in full,
// inside browser history and every server's access log (a URL query
// param is the only way to authenticate a plain <img>/<iframe> src, which
// can't carry an Authorization header). Whoever could read either of
// those got a fully-privileged credential good for 24 hours, not just a
// way to re-fetch that one image.
//
// Instead, getAssetUrl now attaches a SEPARATE, narrowly-scoped,
// short-lived (~10 minutes) token — minted by GET /asset-token (see
// IssueAssetTokenHandler's own doc comment,
// internal/handlers/asset_token_handler.go) — that the backend refuses to
// accept for anything OTHER than /uploads and /img. A copy left behind in
// history/logs is now both useless for calling any real API and stale
// within minutes.
//
// Kept in memory only (module-level, never localStorage) — it's cheap to
// re-mint and has no reason to survive a page reload. AuthContext.js
// mints one right after login and re-mints it every 8 minutes while
// signed in (comfortably inside the backend's 10-minute expiry), so by
// the time any page actually renders an <img>, a fresh token is already
// cached here — getAssetUrl itself stays a plain synchronous function
// (an <img src> can't await a promise), it just reads whatever
// refreshAssetToken last cached.
let assetToken = null;
let assetTokenRefreshPromise = null;

export async function refreshAssetToken() {
  if (assetTokenRefreshPromise) return assetTokenRefreshPromise;
  assetTokenRefreshPromise = api
    .get("/asset-token")
    .then((res) => {
      assetToken = res.data?.token || null;
      return assetToken;
    })
    .catch((err) => {
      console.warn("Failed to refresh asset token:", err.message);
      return assetToken;
    })
    .finally(() => {
      assetTokenRefreshPromise = null;
    });
  return assetTokenRefreshPromise;
}

// Called on logout (AuthContext.js) so a cached asset token can never
// outlive the session that minted it, even in memory.
export function clearAssetToken() {
  assetToken = null;
}

export function getAssetUrl(path) {
  if (!path) return path;
  // Already absolute (external URL, or a local blob:/data: preview from a
  // freshly-picked file that hasn't been uploaded yet) — leave it alone,
  // nothing to prefix or attach a token to.
  if (/^(https?:|blob:|data:)/i.test(path)) return path;

  const base = `${ASSET_BASE_URL.replace(/\/$/, "")}${path}`;
  if (!assetToken) return base;
  return `${base}${base.includes("?") ? "&" : "?"}token=${encodeURIComponent(assetToken)}`;
}

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// >>> منطق البحث والباجيناشن الجديد (معزول) <<<
const buildQuery = (params) => {
  if (!params || typeof params !== 'object') return "";
  const query = new URLSearchParams(params).toString();
  return query ? `?${query}` : "";
};

async function request(endpoint, method = "GET", token = null, body = null) {
  const headers = {};
  
  if (body && !(body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${endpoint}`, {
    method,
    headers,
    body: body instanceof FormData ? body : (body ? JSON.stringify(body) : null),
  });

  // --- التعامل مع انتهاء السيشن ---
  if (res.status === 401) {
    const errorData = await res.json().catch(() => ({}));
    if (errorData.code === "TOKEN_EXPIRED" || errorData.error === "invalid token") {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login?expired=true";
      return; 
    }
    const customError = new Error(errorData.error || "Unauthorized");
    customError.data = errorData;
    throw customError;
  }

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const customError = new Error(errorData.error || "Server Error");
    customError.data = errorData;
    throw customError;
  }

  return res.json();
}

// Auth
// identifier can be a username, email, or phone number — whichever the
// account actually has set (see the backend's Login handler).
export const login = (identifier, password) => request("/login", "POST", null, { identifier, password });
export const logout = (token) => request("/logout", "POST", token);

// Users
export const getUsers = (token, params) => request(`/users${buildQuery(params)}`, "GET", token);
export const getUserById = (token, id) => request(`/users/${id}`, "GET", token);
export const createUser = (token, data) => request("/users", "POST", token, data);
export const updateUser = (token, id, data) => request(`/users/${id}`, "PUT", token, data);
export const deleteUser = (token, id) => request(`/users/${id}`, "DELETE", token);
export const getUserProfile = (token) => request(`/profile`, "GET", token);
export const updateUserProfile = (token, data) => request("/profile", "PUT", token, data);
// Sets/changes User.ApprovalPINHash — see SetApprovalPINHandler. data is
// { current_password, new_pin }.
export const setApprovalPin = (token, data) => request("/profile/approval-pin", "PUT", token, data);

// Groups
export const getGroups = (token, params) => request(`/groups${buildQuery(params)}`, "GET", token);
export const getGroupById = (token, id) => request(`/groups/${id}`, "GET", token);
export const createGroup = (token, data) => request("/groups", "POST", token, data);
export const updateGroup = (token, id, data) => request(`/groups/${id}`, "PUT", token, data);
export const deleteGroup = (token, id) => request(`/groups/${id}`, "DELETE", token);

// Permissions
export const getPermissions = (token, params) => request(`/permissions${buildQuery(params)}`, "GET", token);
export const createPermission = (token, data) => request("/permissions", "POST", token, data);
export const updatePermission = (token, id, data) => request(`/permissions/${id}`, "PUT", token, data);
// تم تصحيح السطر التالي (حذف data غير المعرفة)
export const deletePermission = (token, id) => request(`/permissions/${id}`, "DELETE", token);

export const getUserPermissions = (token) => request("/user-permissions", "GET", token);

// First-run guided tour (GuideAvatar.jsx/OnboardingGuide.js's own data
// source — see onboarding_tour_handler.go's GetOnboardingTourHandler/
// CompleteOnboardingTourHandler). Plain tenant "/api" routes, open to any
// authenticated user of THIS tenant, same trust level as /user-permissions
// just above.
export const getOnboardingTour = (token) => request("/onboarding-tour", "GET", token);
export const completeOnboardingTour = (token) => request("/onboarding-tour/complete", "POST", token);

// Config (نفس اللوجيك القديم تماماً لضمان عمل الإعدادات)
export const getConfig = (token) => request("/config", "GET", token);
export const updateConfig = (token, data) => request("/config", "PUT", token, data);
// langCode is only meaningful for a guest (no token yet, e.g. the Login
// screen) — it tells the backend which language to resolve the translated
// system name in, since there's no signed-in user preference to read yet
// (see currentLangCode's guest fallback in entity_translation_handler.go).
// Once signed in the backend ignores it and uses the user's own saved
// preference instead, so passing it unconditionally here is harmless.
export const getPublicConfig = (token, langCode) =>
  request(`/public-config${langCode ? `?lang=${encodeURIComponent(langCode)}` : ""}`, "GET", token);

// Dashboard Stats
// langCode lets Recent Activity's translated event names resolve against
// the client's own currently-active UI language instead of racing the
// signed-in user's saved PreferredLanguage column (see currentLangCode's
// doc comment in entity_translation_handler.go).
export const getDashboardStats = (token, langCode) =>
  request(`/dashboard/stats${langCode ? `?lang=${encodeURIComponent(langCode)}` : ""}`, "GET", token);
export const unlockUser = (token, id) => request(`/users/${id}/unlock`, "POST", token);

// Events
export const getEvents = (token, params) => request(`/events${buildQuery(params)}`, "GET", token);
// langCode: same currentLangCode race as above — pass the client's
// currently-active language explicitly so a translation just saved (or a
// language just switched to) doesn't need a hard refresh to appear.
export const getEventById = (token, id, langCode) =>
  request(`/events/${id}${langCode ? `?lang=${encodeURIComponent(langCode)}` : ""}`, "GET", token);
export const createEvent = (token, data) => request("/events", "POST", token, data);
export const updateEvent = (token, id, data) => request(`/events/${id}`, "PUT", token, data);
export const deleteEvent = (token, id) => request(`/events/${id}`, "DELETE", token);
// Every route this app's own backend actually has registered right now —
// see ListAvailableRoutesHandler's own doc comment — used only to power
// AddEditEvent.js's "pick a route" dropdown.
export const getAvailableEventRoutes = (token) => request("/events/available-routes", "GET", token);

// System Logs — read-only audit trail (see ListActivityLogsHandler's own
// doc comment). No create/edit/delete: this screen only ever lists.
export const getActivityLogs = (token, params) => request(`/activity-logs${buildQuery(params)}`, "GET", token);
// exportActivityLogsCSV/PDF: same filters as getActivityLogs above (params
// is whatever the Logs.js filter bar currently holds — user_id/status/
// search/date_from/date_to), but every matching row in one file instead of
// one paginated page. Same raw-fetch/Blob pattern as downloadReportPDF/
// exportTranslations above, since the response here is a file, not JSON.
export async function exportActivityLogsCSV(token, params) {
  const res = await fetch(`${API_URL}/activity-logs/export/csv${buildQuery(params)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const customError = new Error(errorData.error || "Could not export logs");
    customError.data = errorData;
    throw customError;
  }
  return res.blob();
}
export async function exportActivityLogsPDF(token, params) {
  const res = await fetch(`${API_URL}/activity-logs/export/pdf${buildQuery(params)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const customError = new Error(errorData.error || "Could not generate PDF");
    customError.data = errorData;
    throw customError;
  }
  return res.blob();
}

// Geography: Countries
export const getCountries = (token) => request("/geography/countries", "GET", token);
export const getCountryById = (token, id) => request(`/geography/countries/${id}`, "GET", token);
export const createCountry = (token, data) => request("/geography/countries", "POST", token, data);
export const updateCountry = (token, id, data) => request(`/geography/countries/${id}`, "PUT", token, data);
export const deleteCountry = (token, id) => request(`/geography/countries/${id}`, "DELETE", token);

// Bulk import/export — see ExportCountriesHandler/ImportCountriesHandler in
// geography_handler.go; same "own fetch, not the shared JSON request()"
// pattern as exportCategories/importCategories above, since export's
// response is a file, not JSON.
export async function exportCountries(token) {
  const res = await fetch(`${API_URL}/geography/countries/export`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const customError = new Error(errorData.error || "Could not export countries");
    customError.data = errorData;
    throw customError;
  }
  return res.blob();
}
export const importCountries = (token, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return request("/geography/countries/import", "POST", token, formData);
};

// Geography: Zones
export const getZones = (token, params) => request(`/geography/zones${buildQuery(params)}`, "GET", token);
export const getZoneById = (token, id) => request(`/geography/zones/${id}`, "GET", token);
export const createZone = (token, data) => request("/geography/zones", "POST", token, data);
export const updateZone = (token, id, data) => request(`/geography/zones/${id}`, "PUT", token, data);
export const deleteZone = (token, id) => request(`/geography/zones/${id}`, "DELETE", token);

// Bulk import/export — see ExportZonesHandler/ImportZonesHandler in
// geography_handler.go.
export async function exportZones(token) {
  const res = await fetch(`${API_URL}/geography/zones/export`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const customError = new Error(errorData.error || "Could not export zones");
    customError.data = errorData;
    throw customError;
  }
  return res.blob();
}
export const importZones = (token, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return request("/geography/zones/import", "POST", token, formData);
};

// Geography: Cities
export const getCities = (token, params) => request(`/geography/cities${buildQuery(params)}`, "GET", token);
export const getCityById = (token, id) => request(`/geography/cities/${id}`, "GET", token);
export const createCity = (token, data) => request("/geography/cities", "POST", token, data);
export const updateCity = (token, id, data) => request(`/geography/cities/${id}`, "PUT", token, data);
export const deleteCity = (token, id) => request(`/geography/cities/${id}`, "DELETE", token);

// Bulk import/export — see ExportCitiesHandler/ImportCitiesHandler in
// geography_handler.go. `params` (optional {country_id, zone_id}) lets the
// Cities.js page export just its currently-filtered subset instead of
// always pulling the full ~230k-row seeded world.
export async function exportCities(token, params) {
  const res = await fetch(`${API_URL}/geography/cities/export${buildQuery(params)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const customError = new Error(errorData.error || "Could not export cities");
    customError.data = errorData;
    throw customError;
  }
  return res.blob();
}
export const importCities = (token, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return request("/geography/cities/import", "POST", token, formData);
};

// Geography: GeoZones
export const getGeoZones = (token) => request("/geography/geo-zones", "GET", token);
export const getGeoZoneById = (token, id) => request(`/geography/geo-zones/${id}`, "GET", token);
export const createGeoZone = (token, data) => request("/geography/geo-zones", "POST", token, data);
export const updateGeoZone = (token, id, data) => request(`/geography/geo-zones/${id}`, "PUT", token, data);
export const deleteGeoZone = (token, id) => request(`/geography/geo-zones/${id}`, "DELETE", token);

// Modules — the generic enable/disable + install registry
export const getModules = (token) => request("/modules", "GET", token);
export const toggleModule = (token, id) => request(`/modules/${id}/toggle`, "PATCH", token);
export const installModule = (token, file) => {
  const formData = new FormData();
  formData.append("package", file);
  return request("/modules/install", "POST", token, formData);
};
export const uninstallModule = (token, id) => request(`/modules/${id}`, "DELETE", token);
export const updateModuleWidth = (token, id, width) => request(`/modules/${id}`, "PUT", token, { width });

// Dashboard Layout — each user's own personal drag/resize/group arrangement,
// saved separately per screen-size breakpoint ("mobile" | "tablet" |
// "desktop", defaulting to "desktop" when omitted — see
// internal/handlers/layout_handler.go's resolveBreakpoint) so a phone-sized
// screen doesn't render whatever was arranged for a desktop-sized one.
export const getDashboardLayout = (token, breakpoint = "desktop") => request(`/dashboard/layout${buildQuery({ breakpoint })}`, "GET", token);
export const saveDashboardLayout = (token, data, breakpoint = "desktop") => request(`/dashboard/layout${buildQuery({ breakpoint })}`, "PUT", token, data);
export const resetDashboardLayout = (token, breakpoint = "desktop") => request(`/dashboard/layout${buildQuery({ breakpoint })}`, "DELETE", token);

// POS: Branches
export const getBranches = (token) => request("/branches", "GET", token);
export const getBranchById = (token, id) => request(`/branches/${id}`, "GET", token);
export const createBranch = (token, data) => request("/branches", "POST", token, data);
export const updateBranch = (token, id, data) => request(`/branches/${id}`, "PUT", token, data);
export const deleteBranch = (token, id) => request(`/branches/${id}`, "DELETE", token);

// Ecommerce: Stores (models.Store, ControlDB — an online storefront owned
// by this Company, its own subdomain/custom domain, provisioned async via
// ProvisionStore on the backend; see the posflect project's own
// "ecommerce-storefront-plan.md" doc). No deleteStore — a Store is
// deactivated (is_active: false) via updateStore, never hard-deleted,
// same reasoning as Company itself. subdomain is immutable once created
// (server rejects any attempt to change it), so updateStore never sends
// it — see internal/handlers/store_handler.go's updateStorePayload.
// kind — "store" (online shops) or "website" (company websites); omitted
// returns both. Screens that only make sense for shops (product exclusions,
// reports, the theme gallery) pass "store" explicitly. Company-website plan §3.1.
export const getStores = (token, kind) => request(`/stores${buildQuery(kind ? { kind } : null)}`, "GET", token);
export const getStoreById = (token, id) => request(`/stores/${id}`, "GET", token);
export const createStore = (token, data) => request("/stores", "POST", token, data);
export const updateStore = (token, id, data) => request(`/stores/${id}`, "PUT", token, data);
export const retryStoreProvisioning = (token, id) => request(`/stores/${id}/retry-provisioning`, "POST", token);

// Ecommerce: Stores — Custom Domain (optional, in addition to the
// subdomain above; see models.Store.CustomDomain/CustomDomainStatus and
// internal/platform/provision_customdomain.go). getCustomDomainInfo just
// reports this deployment's own PLATFORM_SERVER_IP so the UI can show the
// merchant which A record to create — it needs no store id. setStoreCustomDomain
// saves/clears the domain (status resets to "pending_dns" server-side, or
// to "none" when cleared). verifyStoreCustomDomain is the manual
// "Verify & Issue Certificate" action — deliberately not automatic, see
// the plan doc's own rationale (Let's Encrypt failed-validation rate limits).
export const getCustomDomainInfo = (token) => request("/stores/custom-domain-info", "GET", token);
export const setStoreCustomDomain = (token, id, data) => request(`/stores/${id}/custom-domain`, "PUT", token, data);
export const verifyStoreCustomDomain = (token, id) => request(`/stores/${id}/custom-domain/verify`, "POST", token);

// Buying / transferring a domain through the platform's Dynadot reseller
// account (handlers/domain_order_handler.go). quote never reveals cost or
// markup; createDomainOrder returns the order with a Paymob payment_url the
// browser must be sent to; the order is then fulfilled automatically.
export const quoteStoreDomain = (token, id, domain, kind) =>
  request(`/stores/${id}/domain/quote?domain=${encodeURIComponent(domain)}&kind=${encodeURIComponent(kind)}`, "GET", token);
export const createStoreDomainOrder = (token, id, data) => request(`/stores/${id}/domain/orders`, "POST", token, data);
export const getStoreDomainOrders = (token, id) => request(`/stores/${id}/domain/orders`, "GET", token);

// Ecommerce: Storefront theme gallery (config.StoreThemes — some free,
// some paid and purchased through the platform's own Paymob billing, see
// internal/handlers/store_theme_handler.go's own doc comment).
// purchaseStoreTheme returns {payment_url, ...} — StoreThemes.js redirects
// the browser to payment_url itself, the same hosted-Paymob-checkout
// pattern the tenant-facing storefront checkout already uses. setStoreTheme
// is the one-click "apply this theme to this store" action; updateStore
// above can also set theme_key (AddEditStore.js's own dropdown), both
// paths are enforced by the exact same backend ownership check.
export const getStoreThemes = (token) => request("/store-themes", "GET", token);
// previewStoreTheme — storefront theme-builder plan, H4 (project doc
// "storefront-theme-builder-plan.md", §3/H4): the gallery's own live
// mini-preview, for the six seed-based themes only (config.
// ThemeBuilderSeeds, H3) — PreviewStoreThemeHandler (store_theme_handler.go)
// 404s for "default"/"minimal"/"boutique" (no seed), which StoreThemes.js
// itself treats as "fall back to the static Header/Hero/Footer components"
// rather than an error to surface to the merchant.
export const previewStoreTheme = (token, themeKey) => request(`/store-themes/${themeKey}/preview`, "GET", token);
export const purchaseStoreTheme = (token, themeKey) => request(`/store-themes/${themeKey}/purchase`, "POST", token);
export const setStoreTheme = (token, storeId, themeKey) => request(`/stores/${storeId}/theme`, "PUT", token, { theme_key: themeKey });

// Ecommerce: Storefront Settings (models.StorefrontSettings — lives in the
// TENANT database, unlike Store above; see that model's own doc comment).
// Get-with-defaults / upsert, same pattern as ReceiptTemplate — no
// deleteStorefrontSettings, since there's nothing meaningful to delete back
// to (GetStorefrontSettingsHandler already returns sane defaults when no row
// has been saved yet).
export const getStorefrontSettings = (token, storeId) => request(`/storefront-settings/${storeId}`, "GET", token);
export const updateStorefrontSettings = (token, storeId, data) => request(`/storefront-settings/${storeId}`, "PUT", token, data);

// Site status (maintenance / coming soon): a signed link on the store's own
// host that lets the owner browse the real site while visitors see the status
// page, and the ready-made status page for a mode (created on first use).
export const createSitePreviewLink = (token, storeId) => request(`/storefront-settings/${storeId}/preview-link`, "POST", token, {});
export const ensureSiteModePage = (token, storeId, mode) => request(`/storefront-settings/${storeId}/site-mode-page`, "POST", token, { mode });
// Creates the ready-made designed "About Us" / "Contact Us" pages of a website
// that it does not have yet (pages: ["about","contact"], both when omitted).
export const ensureSiteInfoPages = (token, storeId, pages) => request(`/storefront-settings/${storeId}/info-pages`, "POST", token, pages ? { pages } : {});
// Public (storefront side): is this preview token valid for this store?
export const verifySitePreviewToken = (previewToken) => request(`/public/site-mode/verify${buildQuery({ token: previewToken })}`, "GET", null);

// "Custom Pages" feature, Phase 1 (plan doc §2.38) — admin-authored
// storefront pages (models.CustomPage): any number per Store, each with
// its own slug/SEO/images/nav+footer toggles/active switch and a
// block-based body (the "blocks" array, stored by the backend as one
// opaque JSON string — see models.CustomPage's own doc comment). Same
// per-Store shape getStorefrontSettings/updateStorefrontSettings above
// already use, just a real list instead of one flat row.
export const listCustomPages = (token, storeId) => request(`/custom-pages/store/${storeId}`, "GET", token);
export const createCustomPage = (token, storeId, data) => request(`/custom-pages/store/${storeId}`, "POST", token, data);
export const getCustomPage = (token, id) => request(`/custom-pages/${id}`, "GET", token);
export const updateCustomPage = (token, id, data) => request(`/custom-pages/${id}`, "PUT", token, data);
export const deleteCustomPage = (token, id) => request(`/custom-pages/${id}`, "DELETE", token);
// seedDemoCustomPages — handlers.SeedDemoCustomPagesHandler: on-demand
// counterpart to the automatic seed every brand-new Store already gets at
// creation (config.SeedDemoCustomPages, store_handler.go's own
// CreateStoreHandler) — for a Store that already existed before this
// feature shipped. Safe to call more than once: each of the three demo
// pages is only added if a page with its own slug doesn't already exist
// for this store (config.SeedDemoCustomPagesForce's own doc comment) —
// the response's {added} count is how many actually were.
export const seedDemoCustomPages = (token, storeId) => request(`/custom-pages/store/${storeId}/seed-demo`, "POST", token);
// kind is "featured", "header_bg", or "block" — see
// UploadCustomPageImageHandler's own doc comment (custom_page_handler.go)
// on why one endpoint covers all three. A "block" upload does NOT get
// saved onto the page record itself (there is no column for it — it lives
// inside one Blocks array entry instead), so it resolves to {path} rather
// than the full page CustomPageEditor.js's other two kinds return.
export const uploadCustomPageImage = (token, id, file, kind) => {
  const formData = new FormData();
  formData.append("image", file);
  formData.append("kind", kind);
  return request(`/custom-pages/${id}/image`, "POST", token, formData);
};

// POS: Sections (a named dining area within a branch, grouping Tables
// below — see models.Section)
export const getSections = (token, params) => request(`/sections${buildQuery(params)}`, "GET", token);
export const getSectionById = (token, id) => request(`/sections/${id}`, "GET", token);
export const createSection = (token, data) => request("/sections", "POST", token, data);
export const updateSection = (token, id, data) => request(`/sections/${id}`, "PUT", token, data);
export const deleteSection = (token, id) => request(`/sections/${id}`, "DELETE", token);

// POS: Tables (one physical table within a Section — see models.Table)
export const getTables = (token, params) => request(`/tables${buildQuery(params)}`, "GET", token);
export const getTableById = (token, id) => request(`/tables/${id}`, "GET", token);
export const createTable = (token, data) => request("/tables", "POST", token, data);
export const updateTable = (token, id, data) => request(`/tables/${id}`, "PUT", token, data);
export const deleteTable = (token, id) => request(`/tables/${id}`, "DELETE", token);

// POS: Categories
export const getCategories = (token) => request("/categories", "GET", token);
export const getCategoryById = (token, id) => request(`/categories/${id}`, "GET", token);
export const createCategory = (token, data) => request("/categories", "POST", token, data);
export const updateCategory = (token, id, data) => request(`/categories/${id}`, "PUT", token, data);
export const deleteCategory = (token, id) => request(`/categories/${id}`, "DELETE", token);

// Bulk import/export — same "own fetch, not the shared JSON request()"
// pattern as exportTranslations/importTranslations above, since export's
// response is a file, not JSON. See ExportCategoriesHandler/
// ImportCategoriesHandler in category_handler.go.
export async function exportCategories(token) {
  const res = await fetch(`${API_URL}/categories/export`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const customError = new Error(errorData.error || "Could not export categories");
    customError.data = errorData;
    throw customError;
  }
  return res.blob();
}
export const importCategories = (token, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return request("/categories/import", "POST", token, formData);
};

// POS: Units of Measure
export const getUnits = (token) => request("/units", "GET", token);
export const getUnitById = (token, id) => request(`/units/${id}`, "GET", token);
export const createUnit = (token, data) => request("/units", "POST", token, data);
export const updateUnit = (token, id, data) => request(`/units/${id}`, "PUT", token, data);
export const deleteUnit = (token, id) => request(`/units/${id}`, "DELETE", token);

// POS: Taxes (individual, reusable tax rate definitions)
export const getTaxes = (token) => request("/taxes", "GET", token);
export const getTaxById = (token, id) => request(`/taxes/${id}`, "GET", token);
export const createTax = (token, data) => request("/taxes", "POST", token, data);
export const updateTax = (token, id, data) => request(`/taxes/${id}`, "PUT", token, data);
export const deleteTax = (token, id) => request(`/taxes/${id}`, "DELETE", token);

// POS: Tax Groups (named bundles of one or more Taxes — this is what a
// Product actually selects)
export const getTaxGroups = (token) => request("/tax-groups", "GET", token);
export const getTaxGroupById = (token, id) => request(`/tax-groups/${id}`, "GET", token);
export const createTaxGroup = (token, data) => request("/tax-groups", "POST", token, data);
export const updateTaxGroup = (token, id, data) => request(`/tax-groups/${id}`, "PUT", token, data);
export const deleteTaxGroup = (token, id) => request(`/tax-groups/${id}`, "DELETE", token);

// POS: Currencies (see docs/currency-design.md). setBaseCurrency is a
// dedicated action, not a plain field update on the record.
export const getCurrencies = (token) => request("/currencies", "GET", token);
export const getCurrencyById = (token, id) => request(`/currencies/${id}`, "GET", token);
export const createCurrency = (token, data) => request("/currencies", "POST", token, data);
export const updateCurrency = (token, id, data) => request(`/currencies/${id}`, "PUT", token, data);
export const deleteCurrency = (token, id) => request(`/currencies/${id}`, "DELETE", token);
export const setBaseCurrency = (token, id) => request(`/currencies/${id}/set-base`, "POST", token);
// "تحديث أسعار الصرف تلقائيًا" (automatic exchange rate updates) — the
// Currencies screen's own "Update Rates Now" button (see
// UpdateExchangeRatesNowHandler on the backend). Returns
// {updated, skipped} — Currencies.js re-fetches the list itself afterward
// rather than trusting this response to carry the fresh rows.
export const updateExchangeRatesNow = (token) => request("/currencies/update-rates", "POST", token);
// Per-currency note/coin values for the Checkout quick-cash pad — see
// models.CurrencyDenomination and CurrencyDenominations.js.
export const getCurrencyDenominations = (token, currencyId) => request(`/currencies/${currencyId}/denominations`, "GET", token);
export const createCurrencyDenomination = (token, currencyId, data) => request(`/currencies/${currencyId}/denominations`, "POST", token, data);
export const updateCurrencyDenomination = (token, id, data) => request(`/currency-denominations/${id}`, "PUT", token, data);
export const deleteCurrencyDenomination = (token, id) => request(`/currency-denominations/${id}`, "DELETE", token);

// POS: Products
export const getProducts = (token, params) => request(`/products${buildQuery(params)}`, "GET", token);
export const getProductById = (token, id) => request(`/products/${id}`, "GET", token);
export const createProduct = (token, data) => request("/products", "POST", token, data);
export const updateProduct = (token, id, data) => request(`/products/${id}`, "PUT", token, data);
export const deleteProduct = (token, id) => request(`/products/${id}`, "DELETE", token);

// Bulk import/export — see ExportProductsHandler/ImportProductsHandler in
// product_handler.go; same pattern as exportCategories/importCategories
// above.
export async function exportProducts(token) {
  const res = await fetch(`${API_URL}/products/export`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const customError = new Error(errorData.error || "Could not export products");
    customError.data = errorData;
    throw customError;
  }
  return res.blob();
}
export const importProducts = (token, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return request("/products/import", "POST", token, formData);
};
// Its own multipart endpoint (see UploadProductImageHandler in
// product_handler.go) rather than folded into create/update, which stay
// plain JSON — call this right after a successful create/update whenever
// the user picked a new image file.
export const uploadProductImage = (token, id, file) => {
  const formData = new FormData();
  formData.append("image", file);
  return request(`/products/${id}/image`, "POST", token, formData);
};

// Ecommerce: per-product store visibility — models.ProductStoreExclusion is
// an EXCLUSION list on the backend ("hidden from these stores"), but the
// UI/API shape here is just "which store refs are currently excluded" —
// GetProductStoreVisibilityHandler/UpdateProductStoreVisibilityHandler
// always deal in that same excluded_store_refs array, never a per-store
// included/excluded pair.
export const getProductStoreVisibility = (token, id) => request(`/products/${id}/store-visibility`, "GET", token);
export const updateProductStoreVisibility = (token, id, excludedStoreRefs) =>
  request(`/products/${id}/store-visibility`, "PUT", token, { excluded_store_refs: excludedStoreRefs });

// Ecommerce: per-product image gallery (models.ProductImage) — ADDITIONAL
// pictures beyond the single cover image (getProductById's own image_url,
// set via uploadProductImage above). Its own multipart upload, same
// reasoning uploadProductImage itself already follows (product create/
// update stay plain JSON); reorderProductImages sends the gallery's
// complete new id order in one call, mirroring updateProductStoreVisibility's
// own "send the whole replacement set" shape.
export const getProductImages = (token, productId) => request(`/products/${productId}/images`, "GET", token);
export const uploadProductImageToGallery = (token, productId, file, altText) => {
  const formData = new FormData();
  formData.append("image", file);
  if (altText) formData.append("alt_text", altText);
  return request(`/products/${productId}/images`, "POST", token, formData);
};
export const deleteProductImage = (token, id) => request(`/product-images/${id}`, "DELETE", token);
export const reorderProductImages = (token, productId, imageIds) =>
  request(`/products/${productId}/images/reorder`, "PUT", token, { image_ids: imageIds });

// Ecommerce: per-product variant catalog (models.VariantOptionType/
// VariantOptionValue/ProductVariant) — CATALOG ONLY this pass, see
// VariantOptionType's own doc comment on the backend: no stock/checkout
// line-item integration yet. createVariantOptionType takes { name, values }
// (values: string[], its initial value set in one call); addVariantOptionValue
// appends a single new value to an existing type later. createProductVariant/
// updateProductVariant both take { sku, barcode, price, cost, is_active,
// option_value_ids } — price/cost null (or omitted) means "inherit from the
// base product", exactly like leaving Product.TaxGroupID unset.
export const getVariantOptionTypes = (token, productId) => request(`/products/${productId}/variant-option-types`, "GET", token);
export const createVariantOptionType = (token, productId, data) => request(`/products/${productId}/variant-option-types`, "POST", token, data);
export const updateVariantOptionType = (token, id, name) => request(`/variant-option-types/${id}`, "PUT", token, { name });
export const deleteVariantOptionType = (token, id) => request(`/variant-option-types/${id}`, "DELETE", token);
export const addVariantOptionValue = (token, typeId, value) => request(`/variant-option-types/${typeId}/values`, "POST", token, { value });
export const deleteVariantOptionValue = (token, id) => request(`/variant-option-values/${id}`, "DELETE", token);
export const getProductVariants = (token, productId) => request(`/products/${productId}/variants`, "GET", token);
export const createProductVariant = (token, productId, data) => request(`/products/${productId}/variants`, "POST", token, data);
export const updateProductVariant = (token, id, data) => request(`/product-variants/${id}`, "PUT", token, data);
export const deleteProductVariant = (token, id) => request(`/product-variants/${id}`, "DELETE", token);
export const generateProductVariantCombinations = (token, productId) => request(`/products/${productId}/variants/generate`, "POST", token);

// POS: Product Quantity Discounts ("buy N or more" tiers) — managed from
// the Product edit screen's own "Quantity Discounts" tab.
export const getProductQuantityDiscounts = (token, productId) => request(`/products/${productId}/quantity-discounts`, "GET", token);
export const createProductQuantityDiscount = (token, productId, data) => request(`/products/${productId}/quantity-discounts`, "POST", token, data);
export const updateProductQuantityDiscount = (token, id, data) => request(`/product-quantity-discounts/${id}`, "PUT", token, data);
export const deleteProductQuantityDiscount = (token, id) => request(`/product-quantity-discounts/${id}`, "DELETE", token);

// POS: Sales (bulk/group promotional discount campaigns — a flat
// percentage off a hand-picked set of products)
export const getSales = (token) => request("/sales", "GET", token);
export const getSaleById = (token, id) => request(`/sales/${id}`, "GET", token);
export const createSale = (token, data) => request("/sales", "POST", token, data);
export const updateSale = (token, id, data) => request(`/sales/${id}`, "PUT", token, data);
export const deleteSale = (token, id) => request(`/sales/${id}`, "DELETE", token);

// POS: Bundle Offers ("buy N, cheapest one free")
export const getBundleOffers = (token) => request("/bundle-offers", "GET", token);
export const getBundleOfferById = (token, id) => request(`/bundle-offers/${id}`, "GET", token);
export const createBundleOffer = (token, data) => request("/bundle-offers", "POST", token, data);
export const updateBundleOffer = (token, id, data) => request(`/bundle-offers/${id}`, "PUT", token, data);
export const deleteBundleOffer = (token, id) => request(`/bundle-offers/${id}`, "DELETE", token);

// Ecommerce: Product Collections (models.ProductCollection) — a
// hand-curated group of products spanning any mix of Categories, e.g.
// "Summer Sale". Distinct from a Category (required, single, hierarchical
// home for a product) — a product can belong to any number of collections,
// or none.
export const getProductCollections = (token) => request("/product-collections", "GET", token);
export const getProductCollectionById = (token, id) => request(`/product-collections/${id}`, "GET", token);
export const createProductCollection = (token, data) => request("/product-collections", "POST", token, data);
export const updateProductCollection = (token, id, data) => request(`/product-collections/${id}`, "PUT", token, data);
export const deleteProductCollection = (token, id) => request(`/product-collections/${id}`, "DELETE", token);

// Ecommerce: Shipping Zones (models.ShippingZone) — this feature's own
// plan doc §3.5/§6 "جولة ثانية عشر". A flat delivery fee per Country,
// charged at storefront checkout (see internal/handlers/
// shipping_zone_handler.go and storefront_checkout_handler.go's own
// resolveShippingFee).
export const getShippingZones = (token) => request("/shipping-zones", "GET", token);
export const getShippingZoneById = (token, id) => request(`/shipping-zones/${id}`, "GET", token);
export const createShippingZone = (token, data) => request("/shipping-zones", "POST", token, data);
export const updateShippingZone = (token, id, data) => request(`/shipping-zones/${id}`, "PUT", token, data);
export const deleteShippingZone = (token, id) => request(`/shipping-zones/${id}`, "DELETE", token);

// Ecommerce: Product Reviews moderation (models.ProductReview) — this
// feature's own plan doc §3.7/§6 "جولة رابعة عشر". status is "pending"
// (default) or "approved" — see ListProductReviewsAdminHandler's own doc
// comment. No create/edit — a review is only ever authored by a shopper
// through the public storefront.
export const getProductReviewsAdmin = (token, status) => request(`/product-reviews${buildQuery(status ? { status } : null)}`, "GET", token);
export const approveProductReview = (token, id) => request(`/product-reviews/${id}/approve`, "POST", token);
export const deleteProductReview = (token, id) => request(`/product-reviews/${id}`, "DELETE", token);

// --- Public Storefront (StorefrontApp) — deliberately UNAUTHENTICATED.
// Every call below passes token=null to request() on purpose: the caller
// is always an anonymous shopper (or App.js's own useSiteContext, before
// it even knows which app tree to mount), never a logged-in tenant user —
// see internal/handlers/storefront_public_handler.go's own top-of-file
// doc comment for the backend side of this same "no JWT at all" design.
// request()'s own 401 handling (redirect to /login) is never hit here in
// practice, since none of these routes ever require a token to begin
// with — see routes.go's own registration of each one directly on the
// bare api/app group, never inside the `protected` group.
export const getSiteContext = () => request("/public/site-context", "GET", null);
// getPublicCategories/getPublicCollections — جولة 29 تكملة (2026-10-02):
// both now take an optional lang code (a storefront visitor's own chosen
// language, StorefrontI18nContext.js) and forward it as ?lang=, the exact
// same query param currentLangCode (entity_translation_handler.go) already
// reads on every OTHER authenticated page in this app — so a category/
// collection an admin gave an alternate name via EntityTranslationsButton
// now shows that name here too, with zero new backend concept.
export const getPublicCategories = (lang) => request(`/public/categories${buildQuery(lang ? { lang } : null)}`, "GET", null);
export const getPublicCollections = (lang) => request(`/public/collections${buildQuery(lang ? { lang } : null)}`, "GET", null);
export const getPublicProducts = (params) => request(`/public/products${buildQuery(params)}`, "GET", null);
// Admin-side live preview of the same two catalog reads (visual page
// builder only) — JWT-protected + scoped to a store this company owns, see
// handlers.withOwnedStorePreview. Same params/response as the public pair.
export const getPreviewProducts = (token, storeId, params) =>
  request(`/stores/${storeId}/preview/products${buildQuery(params)}`, "GET", token);
export const getPreviewCategories = (token, storeId, lang) =>
  request(`/stores/${storeId}/preview/categories${buildQuery(lang ? { lang } : null)}`, "GET", token);
// Company websites (Store.Kind === "website") — company-website plan §3.4.
// Admin CRUD for website content (services / news posts / team members /
// testimonials / gallery images), all one generic WebsiteItem shape.
export const getSiteThemes = (token) => request("/stores/site-themes", "GET", token);
export const getWebsiteItems = (token, storeId, kind) =>
  request(`/stores/${storeId}/website/items${buildQuery(kind ? { kind } : null)}`, "GET", token);
export const createWebsiteItem = (token, storeId, data) => request(`/stores/${storeId}/website/items`, "POST", token, data);
export const updateWebsiteItem = (token, storeId, itemId, data) =>
  request(`/stores/${storeId}/website/items/${itemId}`, "PUT", token, data);
export const deleteWebsiteItem = (token, storeId, itemId) =>
  request(`/stores/${storeId}/website/items/${itemId}`, "DELETE", token);
// Resolves to {path}; the form then sends that path back as image_path.
export const uploadWebsiteItemImage = (token, storeId, file) => {
  const formData = new FormData();
  formData.append("image", file);
  return request(`/stores/${storeId}/website/items/image`, "POST", token, formData);
};
// Admin-side live preview of the website reads (visual page builder only) —
// same params/response as the public pair below, see handlers.PreviewWebsiteItemsHandler.
export const getPreviewWebsiteItems = (token, storeId, params) =>
  request(`/stores/${storeId}/preview/website-items${buildQuery(params)}`, "GET", token);
export const getPreviewWebsiteBranches = (token, storeId) =>
  request(`/stores/${storeId}/preview/website-branches`, "GET", token);
// Public (anonymous, store resolved from the Host header).
export const getPublicWebsiteItems = (params) => request(`/public/website/items${buildQuery(params)}`, "GET", null);
// `lang` (the visitor's language code, optional) makes the server answer with
// the translated title/summary/body/label/button text of an item and the
// translated branch names; a missing translation keeps the original text.
export const getPublicWebsiteItem = (kind, slug, lang) =>
  request(`/public/website/items/${encodeURIComponent(kind)}/${encodeURIComponent(slug)}${buildQuery(lang ? { lang } : null)}`, "GET", null);
export const getPublicWebsiteBranches = (lang) => request(`/public/website/branches${buildQuery(lang ? { lang } : null)}`, "GET", null);
// The website search: { q, limit?, lang? } → { query, total, groups: [{ kind, count, items }] }.
export const searchPublicWebsite = (params) => request(`/public/website/search${buildQuery(params)}`, "GET", null);

// getPublicVariantFilters — §3.19/§6 "جولة ثالثة وعشرون"'s own addition:
// the catalog grid's own variant-filter chip groups (ListPublicVariantFiltersHandler,
// storefront_public_handler.go) — {filters: [{name, values: [{id, value}]}]}.
// params is the same optional {category_id} ListPublicProductsHandler's own
// equivalent filter uses, so the chips shown only ever cover values that
// could actually narrow the grid the shopper is currently looking at.
export const getPublicVariantFilters = (params) => request(`/public/variant-filters${buildQuery(params)}`, "GET", null);
export const getPublicProductBySlug = (slug, lang) =>
  request(`/public/products/${encodeURIComponent(slug)}${buildQuery(lang ? { lang } : null)}`, "GET", null);
// getPublicCollectionBySlug — §3.10/§6 "جولة سابعة عشر"'s own addition,
// the dedicated "/c/:slug" collection landing page's own metadata call
// (StorefrontCollectionPage.js). The product GRID on that same page reuses
// getPublicProducts({ collection_slug }) above instead — see
// GetPublicCollectionBySlugHandler's own doc comment (internal/handlers/
// storefront_seo_handler.go) for why these stay two separate calls.
export const getPublicCollectionBySlug = (slug, lang) =>
  request(`/public/collections/${encodeURIComponent(slug)}${buildQuery(lang ? { lang } : null)}`, "GET", null);
// getPublicCustomPageBySlug — "Custom Pages" feature, Phase 1 (plan doc
// §2.38): StorefrontCustomPage.js's own data call, by the page's own
// per-store-unique slug (models.CustomPage). 404s the same way for
// "missing" and "exists but disabled", same as every other public
// by-slug lookup above. `lang` — the content-translation round's own
// follow-up — is the shopper's current storefront language
// (useStorefrontTranslation()'s own languageCode), threaded straight onto
// applyCustomPageTranslation (custom_page_handler.go) via currentLangCode's
// own ?lang= query param, same "?lang= always wins" convention
// getPublicCollectionBySlug above already uses.
export const getPublicCustomPageBySlug = (slug, lang) =>
  request(`/public/custom-pages/${encodeURIComponent(slug)}${buildQuery(lang ? { lang } : null)}`, "GET", null);
// getPublicCustomPageById — "Custom Pages" feature, Phase 3 (plan doc
// §2.38): StorefrontPolicyPage.js's own data call when one of the six
// fixed info pages has a CustomPage "design" assigned
// (models.StorefrontSettings.*DesignRef) — by numeric id, since that's
// how the assignment itself is stored. Same 404-for-missing-or-disabled
// convention as getPublicCustomPageBySlug above, and the same `lang`
// passthrough.
export const getPublicCustomPageById = (id, lang) =>
  request(`/public/custom-pages/id/${id}${buildQuery(lang ? { lang } : null)}`, "GET", null);

// submitStorefrontContactMessage — "منشئ صفحات متكامل" plan, Phase F (§4
// Layer 4): the Contact Form widget's own submit call
// (handlers.SubmitStorefrontContactMessageHandler, POST
// /api/public/contact-messages). Unauthenticated, same convention as every
// other public write above — the backend sends the merchant a single
// notification email and stores nothing (see that handler's own doc
// comment for why), so this call's only meaningful response is {ok:true}
// or a 400/429 error message to show inline on the form.
export const submitStorefrontContactMessage = (payload) =>
  request("/public/contact-messages", "POST", null, payload);

// Public Storefront cart + checkout — this feature's own plan doc §3.3/§6
// "جولة ثامنة". cartToken is CartContext.js's own token (localStorage,
// never a cookie/JWT — see models.StorefrontCart's own doc comment), sent
// as a plain ?cart_token= query param on GET/DELETE and as a JSON body
// field on POST/PUT, same split request()'s own shape already forces for
// every other endpoint here — no change to request() itself was needed.
//
// customerToken — §3.17/§6 "جولة حادية وعشرون"'s own addition, optional on
// every one of these four exactly like checkoutStorefrontCart's own token
// param below: when CartContext.js has a signed-in shopper's own
// storefront-customer JWT in hand (CustomerAuthContext.js), passing it
// here is what lets StorefrontCustomerOptionalAuth (routes.go) resolve
// StorefrontCart.CustomerID server-side (linkCartToCustomerIfKnown/
// findOrCreateCart, storefront_checkout_handler.go) — the one thing the
// abandoned-cart reminder loop depends on to know who to email. Omitted/
// undefined for a genuine guest, same no-op-when-falsy convention every
// other optional token param here already follows.
export const getStorefrontCart = (cartToken, customerToken) =>
  request(`/public/cart${buildQuery(cartToken ? { cart_token: cartToken } : null)}`, "GET", customerToken || null);
export const addStorefrontCartItem = (data, customerToken) =>
  request("/public/cart/items", "POST", customerToken || null, data);
export const updateStorefrontCartItem = (itemId, data, customerToken) =>
  request(`/public/cart/items/${itemId}`, "PUT", customerToken || null, data);
export const removeStorefrontCartItem = (itemId, cartToken, customerToken) =>
  request(`/public/cart/items/${itemId}${buildQuery({ cart_token: cartToken })}`, "DELETE", customerToken || null);
// token — §3.8/§6 "جولة رابعة عشر"'s own addition: optional, the logged-in
// shopper's own storefront-customer JWT (CustomerAuthContext.js), so the
// resulting Order links to their account (Order.CustomerID) and a
// customer-linked voucher becomes redeemable (resolveVoucherRedemption,
// storefront_checkout_handler.go). Omitted/undefined for a genuine guest
// checkout — request() only ever attaches an Authorization header when
// truthy, so this is a no-op change for every call site that doesn't pass
// one.
export const checkoutStorefrontCart = (data, token) => request("/public/checkout", "POST", token || null, data);

// Real payment-gateway integration for the storefront — this feature's own
// plan doc §3.4/§6 "جولة عاشرة". getStorefrontPaymentOptions tells
// StorefrontCheckout.js whether to ever show a "pay by card" choice at all;
// getStorefrontOrderPaymentStatus is that same page's own gateway-return
// polling data source (see its own ?gateway_return=1 handling) — both are
// plain unauthenticated GETs, same convention as every other /public/* call
// above.
export const getStorefrontPaymentOptions = () => request("/public/payment-options", "GET", null);
export const getStorefrontOrderPaymentStatus = (orderId) =>
  request(`/public/orders/${orderId}/payment-status`, "GET", null);

// downloadStorefrontOrderReceiptPDF — this plan's own §3.16/§6 "جولة عشرون"
// addition: a downloadable PDF receipt for one completed storefront order
// (DownloadStorefrontOrderReceiptPDFHandler, storefront_checkout_handler.go).
// Same raw-fetch/Blob pattern as downloadPayslipPDF below, since the
// response here is a file (application/pdf), not JSON — and unauthenticated
// like every other /public/* call above, no token param at all: the order
// id itself is the only thing scoping access, same as
// getStorefrontOrderPaymentStatus just above.
export async function downloadStorefrontOrderReceiptPDF(orderId) {
  const res = await fetch(`${API_URL}/public/orders/${orderId}/receipt`);
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const customError = new Error(errorData.error || "Could not download receipt");
    customError.data = errorData;
    throw customError;
  }
  return res.blob();
}

// Buy Now (§4.1) — StorefrontBuyNowPanel.js's own single call, skipping
// /public/cart entirely. Same request()/response shape as
// checkoutStorefrontCart above (either the immediate Cash-on-Delivery
// confirmation, or { requires_payment, redirect_url, order_id } for card).
// token — same optional §3.8 addition as checkoutStorefrontCart above.
export const checkoutStorefrontBuyNow = (data, token) => request("/public/buy-now", "POST", token || null, data);

// Shipping (§3.5/§6 "جولة ثانية عشر") — which countries this store ships
// to, and what each one costs (already converted into this store's own
// effective sale currency — see GetStorefrontShippingOptionsHandler's own
// doc comment). StorefrontCheckout.js and StorefrontBuyNowPanel.js both
// populate their "Delivery country" dropdown from this.
export const getStorefrontShippingOptions = () => request("/public/shipping-options", "GET", null);

// Storefront Customer accounts (§3.8/§6 "جولة رابعة عشر") — a real
// self-service account built on models.Customer (see that model's own
// PasswordHash doc comment). register/login return { token, customer };
// me/updateMe/orders need that token passed through, same convention
// every ADMIN endpoint below already uses request()'s own token param
// for — this is just the storefront's own, separate token, never the
// admin "token" key in localStorage (see CustomerAuthContext.js for
// where this one actually lives).
export const registerStorefrontCustomer = (data) => request("/public/customer/register", "POST", null, data);
export const loginStorefrontCustomer = (data) => request("/public/customer/login", "POST", null, data);
export const getStorefrontCustomerMe = (token) => request("/public/customer/me", "GET", token);
export const updateStorefrontCustomerMe = (token, data) => request("/public/customer/me", "PUT", token, data);
export const getStorefrontCustomerOrders = (token) => request("/public/customer/orders", "GET", token);

// Product Reviews (§3.7/§6 "جولة رابعة عشر") — listing (by product slug)
// is unauthenticated; submitting one needs the same storefront-customer
// token as the account endpoints just above.
export const getPublicProductReviews = (slug) => request(`/public/products/${encodeURIComponent(slug)}/reviews`, "GET", null);
export const submitProductReview = (slug, token, data) =>
  request(`/public/products/${encodeURIComponent(slug)}/reviews`, "POST", token, data);

// POS: Inventory (stock levels + adjustments)
export const getStockLevels = (token, params) => request(`/inventory${buildQuery(params)}`, "GET", token);
export const getStockMovements = (token, params) => request(`/inventory/movements${buildQuery(params)}`, "GET", token);
export const adjustStock = (token, data) => request("/inventory/adjust", "POST", token, data);
export const getProductBatches = (token, params) => request(`/inventory/batches${buildQuery(params)}`, "GET", token);
export const getNearExpiryBatches = (token, params) => request(`/inventory/batches/near-expiry${buildQuery(params)}`, "GET", token);

// System notifications (currently just low-stock alerts) — a single
// global feed any logged-in user can see/acknowledge, see
// models.Notification's doc comment on the backend.
export const getNotifications = (token) => request("/notifications", "GET", token);
export const getUnreadNotificationCount = (token) => request("/notifications/unread-count", "GET", token);
export const markNotificationRead = (token, id) => request(`/notifications/${id}/read`, "PUT", token);
export const markAllNotificationsRead = (token) => request("/notifications/mark-all-read", "PUT", token);

// POS: Customers (optional on every order)
export const getCustomers = (token, params) => request(`/customers${buildQuery(params)}`, "GET", token);
export const getCustomerById = (token, id) => request(`/customers/${id}`, "GET", token);
export const createCustomer = (token, data) => request("/customers", "POST", token, data);
export const updateCustomer = (token, id, data) => request(`/customers/${id}`, "PUT", token, data);
export const deleteCustomer = (token, id) => request(`/customers/${id}`, "DELETE", token);

// Storefront (online) customers — admin directory, scoped to real
// self-service storefront accounts only (see ListStorefrontCustomersHandler's
// own doc comment, storefront_customer_handler.go). Read-only here —
// editing an account (name/phone/active status) still goes through the
// existing updateCustomer/AddEditCustomer.js above, the same shared
// models.Customer row this list is just a filtered, enriched view of.
export const getStorefrontCustomers = (token, params) => request(`/customers/storefront${buildQuery(params)}`, "GET", token);

// POS: Suppliers (who a branch buys stock from — the other side of
// Customers above; see models.Supplier)
export const getSuppliers = (token, params) => request(`/suppliers${buildQuery(params)}`, "GET", token);
export const getSupplierById = (token, id) => request(`/suppliers/${id}`, "GET", token);
export const createSupplier = (token, data) => request("/suppliers", "POST", token, data);
export const updateSupplier = (token, id, data) => request(`/suppliers/${id}`, "PUT", token, data);
export const deleteSupplier = (token, id) => request(`/suppliers/${id}`, "DELETE", token);

// POS: Purchase Orders (a branch's order to buy stock from a Supplier —
// see models.PurchaseOrder). No update() — once created, a PO is only ever
// received or cancelled, never rewritten in place.
export const getPurchaseOrders = (token, params) => request(`/purchase-orders${buildQuery(params)}`, "GET", token);
export const getPurchaseOrderById = (token, id) => request(`/purchase-orders/${id}`, "GET", token);
export const createPurchaseOrder = (token, data) => request("/purchase-orders", "POST", token, data);
export const receivePurchaseOrder = (token, id) => request(`/purchase-orders/${id}/receive`, "POST", token);
export const cancelPurchaseOrder = (token, id) => request(`/purchase-orders/${id}/cancel`, "POST", token);

// POS: Stock Transfers (moves stock from one branch to another — see
// models.StockTransfer). No update() — once created, a transfer is only
// ever completed or cancelled, never rewritten in place.
export const getStockTransfers = (token, params) => request(`/stock-transfers${buildQuery(params)}`, "GET", token);
export const getStockTransferById = (token, id) => request(`/stock-transfers/${id}`, "GET", token);
export const createStockTransfer = (token, data) => request("/stock-transfers", "POST", token, data);
export const completeStockTransfer = (token, id) => request(`/stock-transfers/${id}/complete`, "POST", token);
export const cancelStockTransfer = (token, id) => request(`/stock-transfers/${id}/cancel`, "POST", token);

// POS: Customer loyalty points + wallet — see loyalty_handler.go. Redeem
// converts points into wallet balance (points is optional — omit/0 redeems
// everything the customer currently has); adjust is a manual signed
// top-up/deduction to the wallet balance itself.
export const getLoyaltyTransactions = (token, customerId) => request(`/customers/${customerId}/loyalty/transactions`, "GET", token);
export const redeemLoyaltyPoints = (token, customerId, points) => request(`/customers/${customerId}/loyalty/redeem`, "POST", token, { points });
export const getWalletTransactions = (token, customerId) => request(`/customers/${customerId}/wallet/transactions`, "GET", token);
export const adjustWallet = (token, customerId, data) => request(`/customers/${customerId}/wallet/adjust`, "POST", token, data);

// Custom Fields — admin-configurable extra fields on a page (currently
// only entityType "customer" is wired into any page's rendering) — see
// custom_field_handler.go. Definitions are the field schema (label, type,
// options, width, order); values are one record's answers, keyed by
// definition id.
export const getCustomFieldDefinitions = (token, entityType) => request(`/custom-fields/definitions?entity_type=${encodeURIComponent(entityType)}`, "GET", token);
export const createCustomFieldDefinition = (token, data) => request("/custom-fields/definitions", "POST", token, data);
export const updateCustomFieldDefinition = (token, id, data) => request(`/custom-fields/definitions/${id}`, "PUT", token, data);
export const deleteCustomFieldDefinition = (token, id) => request(`/custom-fields/definitions/${id}`, "DELETE", token);
export const reorderCustomFieldDefinitions = (token, items) => request("/custom-fields/definitions/reorder", "PUT", token, { items });
export const getCustomFieldValues = (token, entityType, entityId) => request(`/custom-fields/values?entity_type=${encodeURIComponent(entityType)}&entity_id=${entityId}`, "GET", token);
export const setCustomFieldValues = (token, entityType, entityId, values) => request("/custom-fields/values", "PUT", token, { entity_type: entityType, entity_id: entityId, values });
export const getCustomFieldReferenceTables = (token) => request("/custom-fields/reference-tables", "GET", token);
export const getCustomFieldReferenceOptions = (token, definitionId, parentValue) => {
  const qs = parentValue !== undefined && parentValue !== null && parentValue !== "" ? `?parent_value=${encodeURIComponent(parentValue)}` : "";
  return request(`/custom-fields/definitions/${definitionId}/options${qs}`, "GET", token);
};
// Native field layout — the SAME drag-and-drop canvas (CustomFields.js)
// also lets an admin reposition/resize a page's own BUILT-IN fields (e.g.
// Customer's Name/Phone/Email) alongside real Custom Fields, without ever
// letting them be renamed, retyped, or deleted — see
// custom_field_handler.go's nativeEntityFields registry.
export const getNativeFieldLayout = (token, entityType) => request(`/custom-fields/native-fields?entity_type=${encodeURIComponent(entityType)}`, "GET", token);
export const reorderNativeFieldLayout = (token, entityType, items) => request("/custom-fields/native-fields/reorder", "PUT", token, { entity_type: entityType, items });

// Offline mode connectivity probe (this feature's own plan doc, "Real
// Offline Mode for the Cashier/Checkout Screen") — see PingHandler's own doc
// comment in ping_handler.go for why this is JWT-gated rather than public.
export const ping = (token) => request("/ping", "GET", token);

// POS: Shifts (cash register sessions) — every Order must belong to one
export const getShifts = (token, params) => request(`/shifts${buildQuery(params)}`, "GET", token);
export const getShiftById = (token, id) => request(`/shifts/${id}`, "GET", token);
export const getCurrentShift = (token) => request("/shifts/current", "GET", token);
export const openShift = (token, data) => request("/shifts/open", "POST", token, data);
export const closeShift = (token, id, data) => request(`/shifts/${id}/close`, "POST", token, data);

// POS: Orders (checkout + sales history)
export const getOrders = (token, params) => request(`/orders${buildQuery(params)}`, "GET", token);
export const getOrderById = (token, id) => request(`/orders/${id}`, "GET", token);
// Resolves the temporary reference a receipt shows when it was printed by
// the offline local print hub (this feature's own plan doc: "Offline
// Receipt Printing via a Local Print Hub + Per-Branch Auto-Print Toggle")
// back to the real, synced order — see GetOrderByClientRequestIDHandler.
export const getOrderByClientRequestId = (token, ref) => request(`/orders/by-client-request-id/${encodeURIComponent(ref)}`, "GET", token);
export const createOrder = (token, data) => request("/orders", "POST", token, data);
export const cancelOrder = (token, id, data) => request(`/orders/${id}/cancel`, "POST", token, data);
// Returns — the proper refund/return flow for a COMPLETED order (cancelOrder
// above only ever applies to a still-held cart). See return_handler.go.
export const getOrderReturns = (token, orderId) => request(`/orders/${orderId}/returns`, "GET", token);
export const createReturn = (token, orderId, data) => request(`/orders/${orderId}/returns`, "POST", token, data);
// Kitchen status — only meaningful for a dine-in Order (see
// UpdateKitchenStatusHandler in order_handler.go); moves it forward
// (pending -> preparing -> ready -> served).
export const updateOrderKitchenStatus = (token, id, status) => request(`/orders/${id}/kitchen-status`, "POST", token, { status });
// Kitchen Display board's own feed — see ListKitchenOrdersHandler's own doc
// comment for why this is separate from getOrders above (gated by
// "order_kitchen_update" instead of "order_view").
export const getKitchenQueue = (token, params) => request(`/orders/kitchen-queue${buildQuery(params)}`, "GET", token);
// Delivery status — only meaningful for a delivery Order (see
// UpdateDeliveryStatusHandler in order_handler.go); moves it forward
// (pending -> assigned -> out_for_delivery -> delivered, or failed).
// deliveryDriverId is only ever needed the first time status is
// "assigned" — every later call can omit it.
export const updateOrderDeliveryStatus = (token, id, status, deliveryDriverId) =>
  request(`/orders/${id}/delivery-status`, "POST", token, deliveryDriverId ? { status, delivery_driver_id: deliveryDriverId } : { status });
// Delivery Queue board's own feed — see ListDeliveryOrdersHandler's own doc
// comment for why this is separate from getOrders/getKitchenQueue above
// (gated by "order_delivery_update" instead of "order_view"/
// "order_kitchen_update").
export const getDeliveryQueue = (token, params) => request(`/orders/delivery-queue${buildQuery(params)}`, "GET", token);
// Real external shipping-carrier booking (Aramex today) for one Order —
// see carrier_shipment_handler.go on the backend. destination is
// { city, country_code, phone?, email? } — only required the first time a
// given order is shipped (its own city/country_code aren't on file
// anywhere else; see carrierDestinationInput's own doc comment).
export const createCarrierShipment = (token, orderId, destination) =>
  request(`/orders/${orderId}/carrier-shipment`, "POST", token, destination);
export const getCarrierShipment = (token, orderId) => request(`/orders/${orderId}/carrier-shipment`, "GET", token);
export const refreshCarrierShipmentTracking = (token, orderId) =>
  request(`/orders/${orderId}/carrier-shipment/refresh`, "POST", token);

// Real payment gateway integration (Network International/N-Genius,
// Paymob) — see order_payment_handler.go on the backend. Card checkout
// itself still goes through createOrder above unchanged; these back the
// "waiting on the gateway" screen Checkout.js switches to once createOrder
// comes back with status "awaiting_payment_confirmation".
// getPaymentGatewayStatus is a cashier-visible check (gated by
// "order_create", not "config_view") of whether a gateway is active for a
// given currency — used on mount so a deployment that never configures one
// keeps today's exact unverified-"card" behavior with zero visible change.
export const getPaymentGatewayStatus = (token, currencyCode) =>
  request(`/payment-gateways/status${buildQuery({ currency_code: currencyCode })}`, "GET", token);
export const getOrderPaymentStatus = (token, orderId) => request(`/orders/${orderId}/payment-status`, "GET", token);
export const retryOrderPayment = (token, orderId) => request(`/orders/${orderId}/retry-payment`, "POST", token);
export const cancelPendingPayment = (token, orderId) => request(`/orders/${orderId}/cancel-pending-payment`, "POST", token);

// Multi-level approval workflows (مسارات اعتماد متعددة المستويات) — see
// models.ApprovalRequest on the backend. createOrder/cancelOrder/
// cancelPendingPayment above can all now come back with a 202
// { approval_required: true, approval_request_id, ... } instead of their
// usual success shape (still resolved, not thrown, by request()'s own
// res.ok check) — Checkout.js polls getApprovalRequest below until it sees
// Approved/Rejected. approveApprovalRequest/rejectApprovalRequest are the
// "any screen" remote path (Approvals.js); resolveApprovalRequestWithPin is
// the in-person path, called from the CASHIER's own session with the
// manager's own PIN.
export const getApprovalRequests = (token, params) => request(`/approval-requests${buildQuery(params)}`, "GET", token);
export const getApprovalRequest = (token, id) => request(`/approval-requests/${id}`, "GET", token);
export const approveApprovalRequest = (token, id) => request(`/approval-requests/${id}/approve`, "POST", token);
export const rejectApprovalRequest = (token, id, reason) => request(`/approval-requests/${id}/reject`, "POST", token, { reason });
export const resolveApprovalRequestWithPin = (token, id, pin, approved, reason) =>
  request(`/approval-requests/${id}/resolve-pin`, "POST", token, { pin, approved, reason });

// الحضور والانصراف والرواتب (attendance/clock-in-out + payroll) — see
// models.AttendanceRecord/LeaveRequest/PayrollPeriod on the backend.
// clockAttendance needs no permission at all — the target user_id's own
// PIN is the real authorization, same convention resolveApprovalRequestWithPin
// above already follows for the in-person approval PIN.
export const clockAttendance = (token, userId, branchId, pin) =>
  request("/attendance/clock", "POST", token, { user_id: userId, branch_id: branchId, pin });
export const getAttendanceStatus = (token, userId) =>
  request(`/attendance/status${buildQuery(userId ? { user_id: userId } : {})}`, "GET", token);
export const getAttendanceRecords = (token, params) => request(`/attendance${buildQuery(params)}`, "GET", token);
export const updateAttendanceRecord = (token, id, data) => request(`/attendance/${id}`, "PUT", token, data);

// Leave requests — createLeaveRequest needs no permission (any
// authenticated user, for their own request); approve/reject are gated
// server-side by canManageLeaveRequest (leave_manage OR branch Manager OR
// all_branches_access).
export const createLeaveRequest = (token, data) => request("/leave-requests", "POST", token, data);
export const getLeaveRequests = (token, params) => request(`/leave-requests${buildQuery(params)}`, "GET", token);
export const approveLeaveRequest = (token, id) => request(`/leave-requests/${id}/approve`, "POST", token);
export const rejectLeaveRequest = (token, id, reason) => request(`/leave-requests/${id}/reject`, "POST", token, { reason });

// Payroll — generate/list/get/finalize a PayrollPeriod, add a manual
// PayslipAdjustment (all gated "payroll_manage" server-side), plus the two
// self-service endpoints (getMyPayslips/downloadPayslipPDF, no permission
// needed for your own finalized payslips).
export const generatePayrollPeriod = (token, data) => request("/payroll/periods", "POST", token, data);
export const getPayrollPeriods = (token, params) => request(`/payroll/periods${buildQuery(params)}`, "GET", token);
export const getPayrollPeriod = (token, id) => request(`/payroll/periods/${id}`, "GET", token);
export const finalizePayrollPeriod = (token, id) => request(`/payroll/periods/${id}/finalize`, "POST", token);
export const addPayslipAdjustment = (token, payslipId, data) => request(`/payroll/payslips/${payslipId}/adjustments`, "POST", token, data);
export const getMyPayslips = (token) => request("/payroll/payslips/mine", "GET", token);
// downloadPayslipPDF: same raw-fetch/Blob pattern as downloadReportPDF
// above, since the response here is a file (application/pdf), not JSON.
export async function downloadPayslipPDF(token, payslipId) {
  const res = await fetch(`${API_URL}/payroll/payslips/${payslipId}/pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const customError = new Error(errorData.error || "Could not download payslip");
    customError.data = errorData;
    throw customError;
  }
  return res.blob();
}

// Sets User.AttendancePINHash — see SetAttendancePINHandler's own doc
// comment on the backend. Mirrors setApprovalPin above exactly.
export const setAttendancePin = (token, data) => request("/profile/attendance-pin", "PUT", token, data);

// Purchase Vouchers — see voucher_handler.go.
// getVoucherByCode: read-only lookup by code, used by the Checkout screen
// to preview a voucher's balance/currency/customer before applying it as
// a payment.
export const getVoucherByCode = (token, code) => request(`/vouchers/${encodeURIComponent(code)}`, "GET", token);
// getVouchers/getVoucherTransactions: the admin/manager Vouchers screen —
// every voucher ever issued, and one voucher's full ledger.
export const getVouchers = (token, params) => request(`/vouchers${buildQuery(params)}`, "GET", token);
export const getVoucherTransactions = (token, id) => request(`/vouchers/${id}/transactions`, "GET", token);
// sellVoucher: Checkout's "Sell a Voucher" action — issues a brand-new
// voucher for real money, separate from a normal product sale.
export const sellVoucher = (token, data) => request("/vouchers", "POST", token, data);

// Accounting: Chart of Accounts — see account_handler.go. A "system"
// account (system_key set) has its Type locked server-side but stays
// otherwise fully editable (Code/Name/ParentID/IsActive).
export const getChartOfAccounts = (token) => request("/accounts", "GET", token);
export const getChartOfAccountById = (token, id) => request(`/accounts/${id}`, "GET", token);
export const createChartOfAccount = (token, data) => request("/accounts", "POST", token, data);
export const updateChartOfAccount = (token, id, data) => request(`/accounts/${id}`, "PUT", token, data);
export const deleteChartOfAccount = (token, id) => request(`/accounts/${id}`, "DELETE", token);

// Accounting: Journal Entries — manual entries only (sale/return/
// shift-close/expense entries post themselves automatically). Never
// edited/deleted once posted — voidJournalEntry posts a reversing entry
// instead. See journal_entry_handler.go.
export const getJournalEntries = (token, params) => request(`/journal-entries${buildQuery(params)}`, "GET", token);
export const getJournalEntryById = (token, id) => request(`/journal-entries/${id}`, "GET", token);
export const createJournalEntry = (token, data) => request("/journal-entries", "POST", token, data);
export const voidJournalEntry = (token, id, reason) => request(`/journal-entries/${id}/void`, "POST", token, { reason });

// Accounting: Expense Categories — see expense_category_handler.go.
export const getExpenseCategories = (token) => request("/expense-categories", "GET", token);
export const getExpenseCategoryById = (token, id) => request(`/expense-categories/${id}`, "GET", token);
export const createExpenseCategory = (token, data) => request("/expense-categories", "POST", token, data);
export const updateExpenseCategory = (token, id, data) => request(`/expense-categories/${id}`, "PUT", token, data);
export const deleteExpenseCategory = (token, id) => request(`/expense-categories/${id}`, "DELETE", token);

// Accounting: Expenses — each one auto-posts its own journal entry on
// creation (see expense_handler.go's CreateExpenseHandler), so this is a
// multipart form (a receipt image/PDF is optional) rather than plain JSON.
// Never edited once recorded — voidExpense reverses the linked journal
// entry and marks it voided.
export const getExpenses = (token, params) => request(`/expenses${buildQuery(params)}`, "GET", token);
export const getExpenseById = (token, id) => request(`/expenses/${id}`, "GET", token);
export const createExpense = (token, data) => {
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    formData.append(key, value);
  });
  return request("/expenses", "POST", token, formData);
};
export const voidExpense = (token, id, reason) => request(`/expenses/${id}/void`, "POST", token, { reason });

// Services & Appointments module, Phase 1 — see service_handler.go.
// Service is a sellable, non-stock offering; ServiceStaffAssignment says
// which staff can perform it and at what commission (percentage or fixed —
// see models.ServiceStaffAssignment), managed inline on the Service
// edit screen rather than its own separate list page.
export const getServices = (token) => request("/services", "GET", token);
export const getServiceById = (token, id) => request(`/services/${id}`, "GET", token);
export const createService = (token, data) => request("/services", "POST", token, data);
export const updateService = (token, id, data) => request(`/services/${id}`, "PUT", token, data);
export const deleteService = (token, id) => request(`/services/${id}`, "DELETE", token);

// Bulk import/export — see ExportServicesHandler/ImportServicesHandler in
// service_handler.go; same pattern as exportCategories/importCategories
// above.
export async function exportServices(token) {
  const res = await fetch(`${API_URL}/services/export`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const customError = new Error(errorData.error || "Could not export services");
    customError.data = errorData;
    throw customError;
  }
  return res.blob();
}
export const importServices = (token, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return request("/services/import", "POST", token, formData);
};
// Its own multipart endpoint (see UploadServiceImageHandler in
// service_handler.go) rather than folded into create/update, which stay
// plain JSON — call this right after a successful create/update whenever
// the user picked a new image file. Mirrors uploadProductImage exactly.
export const uploadServiceImage = (token, id, file) => {
  const formData = new FormData();
  formData.append("image", file);
  return request(`/services/${id}/image`, "POST", token, formData);
};

export const getServiceStaffAssignments = (token, serviceId) =>
  request(`/services/${serviceId}/staff-assignments`, "GET", token);
export const createServiceStaffAssignment = (token, serviceId, data) =>
  request(`/services/${serviceId}/staff-assignments`, "POST", token, data);
export const updateServiceStaffAssignment = (token, id, data) =>
  request(`/service-staff-assignments/${id}`, "PUT", token, data);
export const deleteServiceStaffAssignment = (token, id) =>
  request(`/service-staff-assignments/${id}`, "DELETE", token);

// Appointments — the booking calendar, deliberately its own resource
// separate from Service/OrderServiceItem (see models.Appointment's own doc
// comment in appointment.go: booking a slot and ringing up the eventual
// sale at Checkout are two different moments in time). params for
// getAppointments: { date: "YYYY-MM-DD", user_id, branch_id, status } — all
// optional, see ListAppointmentsHandler.
export const getAppointments = (token, params) => request(`/appointments${buildQuery(params)}`, "GET", token);
export const getAppointmentById = (token, id) => request(`/appointments/${id}`, "GET", token);
export const createAppointment = (token, data) => request("/appointments", "POST", token, data);
export const updateAppointment = (token, id, data) => request(`/appointments/${id}`, "PUT", token, data);
export const deleteAppointment = (token, id) => request(`/appointments/${id}`, "DELETE", token);

// عيادات الأسنان (dental clinics, Branch.BusinessType "dental_clinic") —
// see models/dental.go's own doc comments on the backend. getDentalChart
// auto-creates the patient's 32-site chart on first open (see
// GetDentalChartHandler); updateToothRecord is the dentist's own manual
// correction of a SITE's IsPrimaryTooth/Notes (not a new treatment — see
// addToothTreatment for that). uploadTreatmentAttachment mirrors
// uploadServiceImage's multipart pattern above exactly, just additive
// (a treatment entry can carry several files) rather than a single-field
// replace.
export const getDentalChart = (token, customerId) => request(`/dental/customers/${customerId}/chart`, "GET", token);
export const updateToothRecord = (token, toothRecordId, data) =>
  request(`/dental/tooth-records/${toothRecordId}`, "PUT", token, data);
export const getToothTreatments = (token, toothRecordId) =>
  request(`/dental/tooth-records/${toothRecordId}/treatments`, "GET", token);
export const addToothTreatment = (token, toothRecordId, data) =>
  request(`/dental/tooth-records/${toothRecordId}/treatments`, "POST", token, data);
export const uploadTreatmentAttachment = (token, entryId, file, label) => {
  const formData = new FormData();
  formData.append("file", file);
  if (label) formData.append("label", label);
  return request(`/dental/treatments/${entryId}/attachments`, "POST", token, formData);
};
export const deleteTreatmentAttachment = (token, entryId, attachmentId) =>
  request(`/dental/treatments/${entryId}/attachments/${attachmentId}`, "DELETE", token);

// docs/العيادات.md item 1 — medical history/allergies. getDentalChart above
// already returns has_medical_alert/medical_profile for the banner; these
// two are the dedicated get/put pair the edit panel uses (getDentalChart
// isn't re-fetched just to open the edit form).
export const getDentalMedicalProfile = (token, customerId) =>
  request(`/dental/customers/${customerId}/medical-profile`, "GET", token);
export const updateDentalMedicalProfile = (token, customerId, data) =>
  request(`/dental/customers/${customerId}/medical-profile`, "PUT", token, data);

// docs/العيادات.md item 2 — treatment planning (proposed vs executed).
// listTreatmentPlanItems returns a patient's full plan-item history
// (proposed/accepted/declined/completed alike, most recent first);
// completeTreatmentPlanItem takes the same branch/performed-by/
// resulting-status shape addToothTreatment does, since finishing a plan
// item is still a real clinical event (see CompleteTreatmentPlanItemHandler's
// own doc comment on the backend).
export const listTreatmentPlanItems = (token, customerId) =>
  request(`/dental/customers/${customerId}/plan-items`, "GET", token);
export const createTreatmentPlanItem = (token, customerId, data) =>
  request(`/dental/customers/${customerId}/plan-items`, "POST", token, data);
export const updateTreatmentPlanItem = (token, planItemId, data) =>
  request(`/dental/plan-items/${planItemId}`, "PUT", token, data);
export const completeTreatmentPlanItem = (token, planItemId, data) =>
  request(`/dental/plan-items/${planItemId}/complete`, "POST", token, data);
export const deleteTreatmentPlanItem = (token, planItemId) =>
  request(`/dental/plan-items/${planItemId}`, "DELETE", token);
// docs/العيادات.md item 3 — printable/signable PDF of the patient's
// currently open treatment plan. Same raw-fetch/Blob pattern as
// downloadPayslipPDF above, since the response here is a file
// (application/pdf), not JSON.
export async function downloadTreatmentPlanPDF(token, customerId) {
  const res = await fetch(`${API_URL}/dental/customers/${customerId}/plan-items/pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const customError = new Error(errorData.error || "Could not download treatment plan");
    customError.data = errorData;
    throw customError;
  }
  return res.blob();
}

// docs/العيادات.md item 6 — signed patient consent/approval forms (the
// "full version": rendered template text + a digital signature). data for
// createConsentForm is {plan_item_id or treatment_entry_id, branch_id,
// witnessed_by_id, signed_by_name, signature_image} — see
// CreateConsentFormHandler's own doc comment on the backend for exactly
// one of plan_item_id/treatment_entry_id being required.
export const listConsentForms = (token, customerId) =>
  request(`/dental/customers/${customerId}/consent-forms`, "GET", token);
export const createConsentForm = (token, customerId, data) =>
  request(`/dental/customers/${customerId}/consent-forms`, "POST", token, data);
// Same raw-fetch/Blob pattern as downloadTreatmentPlanPDF above.
export async function downloadConsentFormPDF(token, consentFormId) {
  const res = await fetch(`${API_URL}/dental/consent-forms/${consentFormId}/pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const customError = new Error(errorData.error || "Could not download consent form");
    customError.data = errorData;
    throw customError;
  }
  return res.blob();
}

// docs/العيادات.md item 7 — external lab case tracking (crown/bridge/
// denture/implant). data for createDentalLabCase is {lab_name, case_type,
// due_date, lab_cost, notes}; updateDentalLabCase takes any subset of
// those plus {status, received_at, tooth_treatment_entry_id} — see
// UpdateDentalLabCaseHandler's own doc comment on the backend for the
// "fitted" status's own requirement.
export const listDentalLabCases = (token, customerId) =>
  request(`/dental/customers/${customerId}/lab-cases`, "GET", token);
export const createDentalLabCase = (token, customerId, data) =>
  request(`/dental/customers/${customerId}/lab-cases`, "POST", token, data);
export const updateDentalLabCase = (token, labCaseId, data) =>
  request(`/dental/lab-cases/${labCaseId}`, "PUT", token, data);
export const deleteDentalLabCase = (token, labCaseId) =>
  request(`/dental/lab-cases/${labCaseId}`, "DELETE", token);

// docs/العيادات.md item 8 — referrals out to another doctor/specialty.
// data for createDentalReferral is {referred_to_name, specialty, reason,
// tooth_record_id, notes}; updateDentalReferral takes any subset of
// those plus {status, completed_at} — see UpdateDentalReferralHandler's
// own doc comment on the backend for the "completed" status's own
// auto-stamping behavior.
export const listDentalReferrals = (token, customerId) =>
  request(`/dental/customers/${customerId}/referrals`, "GET", token);
export const createDentalReferral = (token, customerId, data) =>
  request(`/dental/customers/${customerId}/referrals`, "POST", token, data);
export const updateDentalReferral = (token, referralId, data) =>
  request(`/dental/referrals/${referralId}`, "PUT", token, data);
export const deleteDentalReferral = (token, referralId) =>
  request(`/dental/referrals/${referralId}`, "DELETE", token);

// Reports — see internal/reports (the report catalog itself) and
// internal/handlers/report_handler.go.
// getReportDefinitions: the Reports screen's own picker/search list.
export const getReportDefinitions = (token) => request("/reports", "GET", token);
// getReportColumnLayout/reorderReportColumns: the customize screen — every
// column in a report's fixed catalog with its current visible/sort_order,
// and saving a new arrangement (gated server-side by "report_customize").
export const getReportColumnLayout = (token, key) => request(`/reports/${key}/columns`, "GET", token);
export const reorderReportColumns = (token, key, items) =>
  request(`/reports/${key}/columns/reorder`, "PUT", token, { items });
// runReport: actually running a report with whatever filters apply —
// returns { columns, rows, filter_summary, ... } already trimmed/ordered to
// the currently visible column layout.
export const runReport = (token, key, params) => request(`/reports/${key}/run${buildQuery(params)}`, "GET", token);
// downloadReportPDF: same query as runReport, but returns the rendered PDF
// itself (application/pdf) rather than JSON — the shared `request` helper
// always parses a JSON body, so this makes its own fetch call instead.
export async function downloadReportPDF(token, key, params) {
  const res = await fetch(`${API_URL}/reports/${key}/pdf${buildQuery(params)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const customError = new Error(errorData.error || "Could not generate PDF");
    customError.data = errorData;
    throw customError;
  }
  return res.blob();
}

// Thermal Receipt Printing (ESC/POS) via Print-Agent — see
// models.PrintAgent/Printer/PrintJob/ReceiptTemplate on the backend and
// this feature's own Phase 1 plan. pairPrintAgent (unauthenticated,
// exchanges a pairing code for a permanent token) is only ever called by
// cmd/printagent itself, never by this frontend — listed here anyway for
// completeness/discoverability.
export const getPrintAgents = (token) => request("/print-agents", "GET", token);
export const createPrintAgent = (token, data) => request("/print-agents", "POST", token, data);
export const deletePrintAgent = (token, id) => request(`/print-agents/${id}`, "DELETE", token);
export const pairPrintAgent = (pairingCode) => request("/print-agents/pair", "POST", null, { pairing_code: pairingCode });
// Local print hub config ("Offline Receipt Printing via a Local Print Hub"
// plan) — updatePrintAgentLocalConfig toggles LocalPrintEnabled and sets the
// LAN URL cmd/printagent's HTTPS server is reachable at;
// regenerateLocalHubToken issues a brand-new LocalHubToken, returned exactly
// once in the response, the same "shown once" convention as the pairing
// code above.
export const updatePrintAgentLocalConfig = (token, id, data) => request(`/print-agents/${id}/local-config`, "PUT", token, data);
export const regenerateLocalHubToken = (token, id) => request(`/print-agents/${id}/regenerate-local-token`, "POST", token);

// Printers — ESC/POS printers assigned to a PrintAgent, reachable over the
// network or by USB.
export const getPrinters = (token, params) => request(`/printers${buildQuery(params)}`, "GET", token);
export const getPrinterById = (token, id) => request(`/printers/${id}`, "GET", token);
export const createPrinter = (token, data) => request("/printers", "POST", token, data);
export const updatePrinter = (token, id, data) => request(`/printers/${id}`, "PUT", token, data);
export const deletePrinter = (token, id) => request(`/printers/${id}`, "DELETE", token);

// Cash Drawer — Checkout.js's own "Open Drawer" button. printerId is
// optional; omitted, the backend resolves the branch's own cash-drawer-
// equipped printer (see OpenCashDrawerHandler on the backend).
export const openCashDrawer = (token, branchId, printerId) =>
  request("/cash-drawer/open", "POST", token, { branch_id: branchId, ...(printerId ? { printer_id: printerId } : {}) });

// Receipt Template — structured toggle/text fields (see
// models.ReceiptTemplate's own doc comment on the backend). Omit branchId
// for the global default row; pass one to edit that branch's own override.
export const getReceiptTemplate = (token, branchId) =>
  request(`/receipt-template${buildQuery(branchId ? { branch_id: branchId } : null)}`, "GET", token);
export const saveReceiptTemplate = (token, data) => request("/receipt-template", "PUT", token, data);
// Uploads a logo image and returns its path — independent of any specific
// template row (see UploadReceiptLogoHandler's own doc comment on the
// backend: the global-default row may not even exist yet). The returned
// logo_path travels in the next saveReceiptTemplate call like any other
// field, rather than being persisted immediately.
export const uploadReceiptLogo = (token, file) => {
  const formData = new FormData();
  formData.append("logo", file);
  return request("/receipt-template/logo", "POST", token, formData);
};

// Manual "Print Receipt" — OrderDetail.js's own print button. printerId is
// optional; omitted, the backend resolves the branch's own default printer.
export const printOrderReceipt = (token, orderId, printerId) =>
  request(`/orders/${orderId}/print`, "POST", token, printerId ? { printer_id: printerId } : {});

// Print Jobs — the admin's own view/retry/delete control over the PrintJob
// queue (see print_job_handler.go on the backend). params supports
// { printer_id, status } to narrow the list; PrintJobs.js's own filters use
// both, and Printers.js links here with ?printer_id= preset when a delete
// is blocked by "still has print jobs on record".
export const getPrintJobs = (token, params) => request(`/print-jobs${buildQuery(params)}`, "GET", token);
export const retryPrintJob = (token, id) => request(`/print-jobs/${id}/retry`, "POST", token);
export const deletePrintJob = (token, id) => request(`/print-jobs/${id}`, "DELETE", token);

// Backups — "نسخ احتياطي واستعادة تلقائية" (see backup_handler.go/
// backup_loop.go on the backend). listBackups shows every row, success or
// failure alike, newest first. runBackupNow/restoreBackup both just kick
// off a background job and return {status:"started"} — Backups.js polls
// listBackups (for a run) or getBackupRestoreStatus (for a restore) the
// same "poll until it settles" pattern CompanyDetail.js's own
// runRetryProvisioning already uses on the platform side. downloadBackup
// is the one raw-fetch/Blob export here, same pattern as
// downloadReportPDF/exportCountries above, since its response is a file,
// not JSON.
export const listBackups = (token) => request("/backups", "GET", token);
export const runBackupNow = (token) => request("/backups/run-now", "POST", token);
export const deleteBackup = (token, id) => request(`/backups/${id}`, "DELETE", token);
// confirmText must be the literal string "RESTORE", typed by hand on the
// Backups screen — see RestoreBackupHandler's own doc comment on the
// backend for why this is the one thing standing between one accidental
// click and permanently overwriting all of this company's own data.
export const restoreBackup = (token, id, confirmText) =>
  request(`/backups/${id}/restore`, "POST", token, { confirm: confirmText });
export const getBackupRestoreStatus = (token) => request("/backups/restore-status", "GET", token);
export async function downloadBackup(token, id) {
  const res = await fetch(`${API_URL}/backups/${id}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const customError = new Error(errorData.error || "Could not download this backup");
    customError.data = errorData;
    throw customError;
  }
  return res.blob();
}

// Barcode label printing — PrintBarcodes.js's own bulk "pick products +
// copies, print" screen (see label_print_handler.go on the backend).
// items is [{ product_id, copies }]; a blank Product.Barcode is
// auto-generated and persisted server-side before the label renders (see
// ensureProductBarcode), so the caller never needs to generate one itself.
// Response is { jobs, item_errors } — best-effort per item, not
// all-or-nothing (see PrintProductLabelsHandler's own doc comment).
export const printProductLabels = (token, printerId, items) =>
  request("/products/print-labels", "POST", token, { printer_id: printerId, items });
// printWeightLabel — the "Weigh & Print" action for a SoldByWeight product
// (see internal/handlers/weight_label_handler.go's own doc comment).
// weightGrams is a plain integer (grams); the caller converts a kg scale
// reading (or a manually-typed kg value) with Math.round(kg * 1000) before
// calling this — see PrintBarcodes.js's own weight-label section.
export const printWeightLabel = (token, { productId, printerId, weightGrams, copies }) =>
  request("/products/print-weight-label", "POST", token, {
    product_id: productId,
    printer_id: printerId,
    weight_grams: weightGrams,
    copies,
  });

// Scale barcode profiles — the configurable weight-barcode layout(s) this
// tenant needs (see internal/models/scale_barcode_profile.go's own doc
// comment). getScaleBarcodeProfiles needs no special permission (any
// logged-in user can read it — Checkout.js's own scan handler depends on
// it); the write calls are gated "printer_manage" server-side.
export const getScaleBarcodeProfiles = (token) => request("/scale-barcode-profiles", "GET", token);
export const createScaleBarcodeProfile = (token, data) => request("/scale-barcode-profiles", "POST", token, data);
export const updateScaleBarcodeProfile = (token, id, data) => request(`/scale-barcode-profiles/${id}`, "PUT", token, data);
export const deleteScaleBarcodeProfile = (token, id) => request(`/scale-barcode-profiles/${id}`, "DELETE", token);

// --- Localization: public lookups (no token needed — the Login page needs
// its own language picker and strings before anyone's signed in). ---
export const getPublicLanguages = () => request("/public/languages", "GET");
export const getPublicTranslations = (namespace, langCode) =>
  request(`/public/translations/${namespace}${buildQuery(langCode ? { lang: langCode } : null)}`, "GET");

// --- Localization: Languages admin CRUD (gated by language_view/create/
// edit/delete — see internal/routes/routes.go). Path keeps the existing
// "/Localization/languages" casing the backend already used before this
// feature had any frontend. ---
export const getLanguages = (token) => request("/Localization/languages", "GET", token);
export const getLanguageById = (token, id) => request(`/Localization/languages/${id}`, "GET", token);
export const createLanguage = (token, data) => request("/Localization/languages", "POST", token, data);
export const updateLanguage = (token, id, data) => request(`/Localization/languages/${id}`, "PUT", token, data);
export const deleteLanguage = (token, id) => request(`/Localization/languages/${id}`, "DELETE", token);

// --- Localization: Translations admin screen (gated by the same
// language_view/language_edit permissions as the Languages CRUD above). ---
export const getTranslationNamespaces = (token) => request("/Localization/translations/namespaces", "GET", token);
export const getNamespaceTranslations = (token, namespace) =>
  request(`/Localization/translations/namespaces/${namespace}`, "GET", token);
export const saveNamespaceTranslations = (token, namespace, data) =>
  request(`/Localization/translations/namespaces/${namespace}`, "PUT", token, data);

// Bulk export: one CSV covering every namespace/key for a single language
// (see ExportTranslationsHandler) — the file an admin fills in (or hands to
// a translator) then feeds back through importTranslations below, instead
// of clicking through every namespace one at a time. Same "own fetch, not
// the shared JSON request()" pattern as downloadReportPDF above, since the
// response here is a file, not JSON.
// Optional 3rd arg `namespace` narrows the CSV to just that one page's own
// translation keys — used by the Export button each individual page (e.g.
// Countries.js) carries alongside the general one on the Translations
// admin screen, which always calls this with namespace left undefined.
export async function exportTranslations(token, langCode, namespace) {
  let url = `${API_URL}/Localization/translations/export?lang=${encodeURIComponent(langCode)}`;
  if (namespace) url += `&namespace=${encodeURIComponent(namespace)}`;
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const customError = new Error(errorData.error || "Could not export translations");
    customError.data = errorData;
    throw customError;
  }
  return res.blob();
}

// Bulk import: upload a CSV (built by exportTranslations, or matching its
// own namespace/key/translation column shape) to bulk-save every row it
// contains for one language in a single request (see
// ImportTranslationsHandler) instead of typing translations into the edit
// grid one key at a time.
export const importTranslations = (token, langCode, file) => {
  const formData = new FormData();
  formData.append("language_code", langCode);
  formData.append("file", file);
  return request("/Localization/translations/import", "POST", token, formData);
};

// --- A user's own language preference (separate from the multipart
// updateUserProfile above on purpose — see UpdateUserPreferredLanguageHandler's
// own doc comment in user_handler.go). ---
export const updatePreferredLanguage = (token, preferredLanguage) =>
  request("/profile/language", "PUT", token, { preferred_language: preferredLanguage });

// --- Localization: per-record "alternate name" translations (Category,
// Product, Branch, the system name, ...) — see EntityTranslation's own doc
// comment in internal/models/language.go. entityType is one of the short
// keys entityTranslationTables (internal/handlers/entity_translation_handler.go)
// whitelists, e.g. "category", "product", "system_setting". Gated by the
// same language_view/language_edit permissions as the two groups above. ---
export const getEntityTranslations = (token, entityType, entityId) =>
  request(`/entity-translations/${entityType}/${entityId}`, "GET", token);
export const saveEntityTranslations = (token, entityType, entityId, translations) =>
  request(`/entity-translations/${entityType}/${entityId}`, "PUT", token, { translations });

// ============================================================================
// Platform (Super Admin panel) — everything below talks to "/platform-api",
// a completely separate route tree from "/api" above (see internal/routes/
// platform_routes.go's own doc comment: registered on the same *fiber.App,
// reached only via the admin.<root-domain> host branch, never touching
// middlewares.TenantResolver or any tenant's own database). Needs its own
// base URL, its own request() equivalent, and its own localStorage keys
// ("platform_token"/"platformUser", never "token"/"user") so a platform-
// staff session and a tenant session can never collide or bleed into each
// other in the same browser — this feature's own plan doc calls that out
// explicitly for PlatformAuthContext/PlatformLogin.js.
const PLATFORM_API_URL = process.env.REACT_APP_PLATFORM_API_URL || `${window.location.origin}/platform-api`;

async function platformRequest(endpoint, method = "GET", token = null, body = null) {
  const headers = {};

  if (body && !(body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${PLATFORM_API_URL}${endpoint}`, {
    method,
    headers,
    body: body instanceof FormData ? body : (body ? JSON.stringify(body) : null),
  });

  if (res.status === 401) {
    // Every one of PlatformAuthProtected's own 401 codes (AUTH_MISSING,
    // AUTH_INVALID, TOKEN_EXPIRED, TOKEN_INVALID — see that middleware's
    // own doc comment) means this platform session is no longer usable,
    // unlike the tenant request()'s narrower check above — there is no
    // "logged in but this one endpoint says no" case here the way a
    // tenant's expired-vs-merely-invalid distinction sometimes matters.
    localStorage.removeItem("platform_token");
    localStorage.removeItem("platformUser");
    window.location.href = "/platform-login?expired=true";
    return;
  }

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const customError = new Error(errorData.error || "Server Error");
    customError.data = errorData;
    throw customError;
  }

  return res.json();
}

// Platform auth
export const platformLogin = (email, password) => platformRequest("/login", "POST", null, { email, password });
export const platformLogout = (token) => platformRequest("/logout", "POST", token);
export const getPlatformUserPermissions = (token) => platformRequest("/user-permissions", "GET", token);

// Platform: Companies (internal/handlers/platform_company_handler.go, task #193)
export const getCompanies = (token, params) => platformRequest(`/companies${buildQuery(params)}`, "GET", token);
export const getCompanyById = (token, id) => platformRequest(`/companies/${id}`, "GET", token);
export const createCompany = (token, data) => platformRequest("/companies", "POST", token, data);
export const renewCompany = (token, id, days) => platformRequest(`/companies/${id}/renew`, "POST", token, { days });
export const suspendCompany = (token, id, reason) => platformRequest(`/companies/${id}/suspend`, "POST", token, { reason });
export const reactivateCompany = (token, id) => platformRequest(`/companies/${id}/reactivate`, "POST", token);
export const retryCompanyProvisioning = (token, id) => platformRequest(`/companies/${id}/retry-provisioning`, "POST", token);
export const forceActivateCompany = (token, id) => platformRequest(`/companies/${id}/force-activate`, "POST", token);
// resetCompanyBusinessAdminPassword / resetCompanyTechnicalAdminPassword —
// this feature's own two-admin split (internal/handlers/
// platform_company_handler.go): the FIRST is the client-facing "Business
// Admin" login (falls back server-side to the legacy "Admin" group for a
// company provisioned before this feature existed); the SECOND is the
// internal/technical "Admin"-group login that's never sent to the client.
export const resetCompanyBusinessAdminPassword = (token, id, newAdminEmail) =>
  platformRequest(`/companies/${id}/reset-admin-password`, "POST", token, newAdminEmail ? { admin_email: newAdminEmail } : undefined);
export const resetCompanyTechnicalAdminPassword = (token, id, newAdminEmail) =>
  platformRequest(`/companies/${id}/reset-technical-admin-password`, "POST", token, newAdminEmail ? { admin_email: newAdminEmail } : undefined);
// updateCompanyTechnicalContact — Company.TechnicalContactEmail (blank
// clears the per-company override, falling back to the platform-wide
// default again).
export const updateCompanyTechnicalContact = (token, id, technicalContactEmail) =>
  platformRequest(`/companies/${id}/technical-contact`, "PUT", token, { technical_contact_email: technicalContactEmail });
// updateCompanyPlan — Company.Plan/MaxBranches/MaxUsers (internal/handlers/
// platform_company_handler.go's UpdateCompanyPlanHandler). max_branches/
// max_users are optional overrides — pass null/undefined to use that
// plan's own default limits (models.DefaultLimitsForPlan).
export const updateCompanyPlan = (token, id, { plan, max_branches, max_users }) =>
  platformRequest(`/companies/${id}/plan`, "PUT", token, { plan, max_branches, max_users });
// cancelCompany / deleteCompany — the two-step "mesh el sherka" (delete company)
// flow (internal/handlers/platform_company_handler.go's CancelCompanyHandler /
// DeleteCompanyHandler): cancel first (reversible — a cancelled company can
// still be Reactivate'd, same as a suspended one), then delete permanently
// (irreversible — only allowed while status is "cancelled" or still
// "provisioning", and only with the exact subdomain typed as confirmation).
export const cancelCompany = (token, id, reason) => platformRequest(`/companies/${id}/cancel`, "POST", token, { reason });
export const deleteCompany = (token, id, confirmSubdomain) =>
  platformRequest(`/companies/${id}`, "DELETE", token, { confirm_subdomain: confirmSubdomain });
// getCompanyDeletionStatus is what CompanyDetail.js's own delete progress
// view polls — deleteCompany above only ever kicks the deletion off now
// (GetCompanyDeletionStatusHandler's own doc comment explains why the
// Company row itself can't be polled the way retryCompanyProvisioning's
// progress view polls getCompanyById).
export const getCompanyDeletionStatus = (token, id) => platformRequest(`/companies/${id}/delete-status`, "GET", token);

// Platform billing — real payment collection for the platform's own
// subscription (internal/handlers/platform_billing_handler.go). Reuses the
// company_renew permission for createCompanyInvoice, same as renewCompany
// above — generating a payment link is just another way of extending a
// subscription.
export const getCompanyInvoices = (token, id) => platformRequest(`/companies/${id}/invoices`, "GET", token);
export const createCompanyInvoice = (token, id, data) => platformRequest(`/companies/${id}/invoices`, "POST", token, data);

// Platform: Users, Groups & Permissions (internal/handlers/platform_user_handler.go, task #202)
export const getPlatformUsers = (token, params) => platformRequest(`/platform-users${buildQuery(params)}`, "GET", token);
export const getPlatformUserById = (token, id) => platformRequest(`/platform-users/${id}`, "GET", token);
export const createPlatformUser = (token, data) => platformRequest("/platform-users", "POST", token, data);
export const updatePlatformUser = (token, id, data) => platformRequest(`/platform-users/${id}`, "PUT", token, data);
export const deletePlatformUser = (token, id) => platformRequest(`/platform-users/${id}`, "DELETE", token);

export const getPlatformGroups = (token) => platformRequest("/platform-groups", "GET", token);
export const getPlatformGroupById = (token, id) => platformRequest(`/platform-groups/${id}`, "GET", token);
export const createPlatformGroup = (token, data) => platformRequest("/platform-groups", "POST", token, data);
export const updatePlatformGroup = (token, id, data) => platformRequest(`/platform-groups/${id}`, "PUT", token, data);
export const deletePlatformGroup = (token, id) => platformRequest(`/platform-groups/${id}`, "DELETE", token);

export const getPlatformPermissions = (token) => platformRequest("/platform-permissions", "GET", token);

// Platform: Settings (internal/handlers/platform_settings_handler.go, task #202)
export const getPlatformSettings = (token) => platformRequest("/settings", "GET", token);
export const updatePlatformSettings = (token, data) => platformRequest("/settings", "PUT", token, data);
export const testDynadotConnection = (token) => platformRequest("/settings/domains/test", "POST", token);
export const getPlatformDomainOrders = (token) => platformRequest("/domain-orders", "GET", token);
export const retryPlatformDomainOrder = (token, id) => platformRequest(`/domain-orders/${id}/retry`, "POST", token);

// Platform: Notifications (internal/handlers/platform_notification_handler.go, task #202)
export const getPlatformNotifications = (token) => platformRequest("/notifications", "GET", token);
export const getPlatformUnreadNotificationCount = (token) => platformRequest("/notifications/unread-count", "GET", token);
export const markPlatformNotificationRead = (token, id) => platformRequest(`/notifications/${id}/read`, "PUT", token);
export const markAllPlatformNotificationsRead = (token) => platformRequest("/notifications/mark-all-read", "PUT", token);

// Platform: Live chat admin inbox (LiveChatInbox.js, gated by
// "livechat_manage" — see internal/handlers/live_chat_handler.go). "/admin"
// in every path here is what keeps these "/:id"-keyed routes from ever
// colliding with the public "/:token"-keyed ones right below.
export const getLiveChatSessions = (token, params) => platformRequest(`/live-chat/admin/sessions${buildQuery(params)}`, "GET", token);
export const getLiveChatSession = (token, id) => platformRequest(`/live-chat/admin/sessions/${id}`, "GET", token);
export const sendLiveChatStaffMessage = (token, id, message) =>
  platformRequest(`/live-chat/admin/sessions/${id}/messages`, "POST", token, { message });
export const closeLiveChatSession = (token, id) => platformRequest(`/live-chat/admin/sessions/${id}/close`, "POST", token);
export const getLiveChatUnreadCount = (token) => platformRequest("/live-chat/admin/unread-count", "GET", token);

// --- Marketing site (posflect.com) — public endpoints (no token: these back
// the marketing site itself, which has no platform session at all — see
// internal/handlers/marketing_content_handler.go / demo_request_handler.go
// and this feature's own plan doc). Registered under "/platform-api" purely
// so they're already exempt from TenantResolver's hostname check; nothing
// here is platform-staff-only. ---
export const getPublicMarketingContent = (langCode) =>
  platformRequest(`/marketing/content${buildQuery(langCode ? { lang: langCode } : null)}`, "GET");
export const getPublicMarketingLanguages = () => platformRequest("/marketing/languages", "GET");
export const submitDemoRequest = (data) => platformRequest("/demo-requests", "POST", null, data);

// Live chat — the marketing site's own sitewide widget (LiveChatWidget.js,
// internal/handlers/live_chat_handler.go). Public, same "no token" reasoning
// as submitDemoRequest above: an anonymous visitor has no platform session
// at all. VisitorToken (the "token" argument below) is what proves "this is
// my own session" in place of a real login — LiveChatWidget.js's own doc
// comment on why it's kept in localStorage rather than reissued per call.
export const startLiveChatSession = (data) => platformRequest("/live-chat/sessions", "POST", null, data);
export const getLiveChatVisitorMessages = (token) =>
  platformRequest(`/live-chat/sessions/${encodeURIComponent(token)}/messages`, "GET", null);
export const sendLiveChatVisitorMessage = (token, message) =>
  platformRequest(`/live-chat/sessions/${encodeURIComponent(token)}/messages`, "POST", null, { message });

// Docs section (posflect.com/docs) — public, unauthenticated, same
// "/platform-api" placement reasoning as the marketing endpoints right
// above (see internal/handlers/doc_page_handler.go). getPublicDocs loads
// the whole hub (every category + its pages' slug/title/summary);
// getPublicDocPage loads one article's full body plus its resolved
// related-pages list.
export const getPublicDocs = (langCode) =>
  platformRequest(`/docs${buildQuery(langCode ? { lang: langCode } : null)}`, "GET");
export const getPublicDocPage = (categorySlug, pageSlug, langCode) =>
  platformRequest(
    `/docs/${encodeURIComponent(categorySlug)}/${encodeURIComponent(pageSlug)}${buildQuery(langCode ? { lang: langCode } : null)}`,
    "GET"
  );

// Blog engagement — comments + ratings on the blog's own articles
// (internal/handlers/doc_comment_handler.go). Public, unauthenticated,
// same "/platform-api" placement reasoning as getPublicDocs/getPublicDocPage
// right above. BlogEngagement.js is the one caller of all three.
export const getDocComments = (categorySlug, pageSlug) =>
  platformRequest(`/docs/${encodeURIComponent(categorySlug)}/${encodeURIComponent(pageSlug)}/comments`, "GET");
export const submitDocComment = (categorySlug, pageSlug, data) =>
  platformRequest(`/docs/${encodeURIComponent(categorySlug)}/${encodeURIComponent(pageSlug)}/comments`, "POST", null, data);
export const submitDocRating = (categorySlug, pageSlug, stars) =>
  platformRequest(`/docs/${encodeURIComponent(categorySlug)}/${encodeURIComponent(pageSlug)}/rating`, "POST", null, { stars });

// --- Marketing content admin editor (MarketingContent.js, gated by
// "marketingcontent_manage" — see marketing_content_handler.go). ---
export const getMarketingContent = (token) => platformRequest("/marketing/admin/content", "GET", token);
export const saveMarketingContent = (token, languageCode, items) =>
  platformRequest("/marketing/admin/content", "PUT", token, { language_code: languageCode, items });

// Bulk export/import — same raw-fetch/Blob pattern as exportTranslations/
// importTranslations above, against the control-plane marketing content
// tables instead of a tenant's own translation tables.
export async function exportMarketingContent(token, langCode) {
  const res = await fetch(`${PLATFORM_API_URL}/marketing/admin/content/export?lang=${encodeURIComponent(langCode)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const customError = new Error(errorData.error || "Could not export marketing content");
    customError.data = errorData;
    throw customError;
  }
  return res.blob();
}
export const importMarketingContent = (token, langCode, file) => {
  const formData = new FormData();
  formData.append("language_code", langCode);
  formData.append("file", file);
  return platformRequest("/marketing/admin/content/import", "POST", token, formData);
};

// Contact phone/WhatsApp — deliberately separate from the section/key/
// language calls above (PlatformSetting-backed, not translated — see
// marketing_content_handler.go's own doc comment on GetMarketingContactHandler/
// UpdateMarketingContactHandler for why a phone number doesn't go through
// the same "default language is code-owned" translation machinery).
export const getMarketingContact = (token) => platformRequest("/marketing/admin/contact", "GET", token);
export const updateMarketingContact = (token, data) => platformRequest("/marketing/admin/contact", "PUT", token, data);

// Business types (MarketingContent.js's "Business types" section) — the one
// place an admin can add/remove a MarketingContentKey rather than only
// translate one; see marketing_content_handler.go's own doc comment on
// CreateBusinessTypeHandler/DeleteBusinessTypeHandler.
export const createBusinessType = (token, label) => platformRequest("/marketing/admin/business-types", "POST", token, { label });
export const deleteBusinessType = (token, slug) => platformRequest(`/marketing/admin/business-types/${encodeURIComponent(slug)}`, "DELETE", token);

// FAQ (MarketingContent.js's "FAQ" section) — same "add/remove rather than
// only translate" exception as business types above, but each entry is a
// {question, answer} pair rather than one label; see
// marketing_content_handler.go's own doc comment on
// CreateFaqHandler/DeleteFaqHandler.
export const createFaq = (token, question, answer) => platformRequest("/marketing/admin/faq", "POST", token, { question, answer });
export const deleteFaq = (token, id) => platformRequest(`/marketing/admin/faq/${encodeURIComponent(id)}`, "DELETE", token);

// --- Platform languages CRUD (PlatformLanguages.js, gated by
// "marketingcontent_manage" — see platform_language_handler.go). ---
export const getPlatformLanguages = (token) => platformRequest("/marketing/admin/languages", "GET", token);
export const createPlatformLanguage = (token, data) => platformRequest("/marketing/admin/languages", "POST", token, data);
export const updatePlatformLanguage = (token, id, data) => platformRequest(`/marketing/admin/languages/${id}`, "PUT", token, data);
export const deletePlatformLanguage = (token, id) => platformRequest(`/marketing/admin/languages/${id}`, "DELETE", token);

// --- Demo requests (DemoRequests.js — the "Request a Demo" funnel's own
// queue, gated per-action by demorequest_view/demorequest_manage/
// demorequest_convert — see demo_request_handler.go). ---
export const getDemoRequests = (token, params) => platformRequest(`/demo-requests${buildQuery(params)}`, "GET", token);
export const getDemoRequestById = (token, id) => platformRequest(`/demo-requests/${id}`, "GET", token);
export const updateDemoRequestStatus = (token, id, status, rejectionReason) =>
  platformRequest(`/demo-requests/${id}/status`, "POST", token, { status, rejection_reason: rejectionReason });
export const convertDemoRequest = (token, id, data) => platformRequest(`/demo-requests/${id}/convert`, "POST", token, data);

// --- Email templates (EmailTemplates.js, gated by "emailtemplate_manage" —
// see email_template_handler.go). Same shape as getMarketingContent/
// saveMarketingContent above, one call loads every template + language at
// once, one call saves one language's subject/body pairs across all of
// them. ---
export const getEmailTemplates = (token) => platformRequest("/email-templates", "GET", token);
export const saveEmailTemplates = (token, languageCode, items) =>
  platformRequest("/email-templates", "PUT", token, { language_code: languageCode, items });

// --- Onboarding tour admin editor (OnboardingTours.js, gated
// "onboardingtour_manage" — see onboarding_tour_handler.go). Same
// languages+business_types+character+steps-in-one-call shape as
// getEmailTemplates above, plus dedicated Create/Update/Delete/Reorder
// actions — unlike EmailTemplate's fixed key set, a step is genuinely
// admin-creatable/deletable/reorderable (see models.OnboardingTourStep's
// own doc comment). ---
export const getOnboardingToursAdmin = (token) => platformRequest("/onboarding-tours", "GET", token);
export const saveOnboardingCharacter = (token, data) => platformRequest("/onboarding-tours/character", "PUT", token, data);
export const createOnboardingTourStep = (token, data) => platformRequest("/onboarding-tours/steps", "POST", token, data);
export const updateOnboardingTourStep = (token, id, data) => platformRequest(`/onboarding-tours/steps/${id}`, "PUT", token, data);
export const deleteOnboardingTourStep = (token, id) => platformRequest(`/onboarding-tours/steps/${id}`, "DELETE", token);
export const reorderOnboardingTourSteps = (token, businessType, orderedIds) =>
  platformRequest("/onboarding-tours/steps/reorder", "PUT", token, { business_type: businessType, ordered_ids: orderedIds });
export const saveOnboardingTourTranslations = (token, languageCode, items) =>
  platformRequest("/onboarding-tours/steps/translations", "PUT", token, { language_code: languageCode, items });

// --- Docs admin editor (DocsAdmin.js, gated by "docs_manage" — see
// doc_page_handler.go). Same shape as getEmailTemplates/saveEmailTemplates
// above: one call loads every category + page + language at once, one call
// saves one language's category name/description and page title/summary/
// body overrides across all of them. Registered under "/docs-admin" (never
// "/docs") on the backend so it can never collide with the public,
// unauthenticated docs routes above. ---
export const getDocsAdmin = (token) => platformRequest("/docs-admin", "GET", token);
export const saveDocs = (token, languageCode, categoryItems, pageItems) =>
  platformRequest("/docs-admin", "PUT", token, {
    language_code: languageCode,
    category_items: categoryItems,
    page_items: pageItems,
  });

// --- Blog comment moderation (BlogComments.js, gated by "blogcomment_manage"
// — see internal/handlers/doc_comment_handler.go). Named "/blog-comments"
// (never nested under "/docs-admin") on the backend so its "/:id" routes
// can never collide with the public "/docs/:category/:slug/comments"
// routes above. ---
export const getDocCommentsAdmin = (token, params) => platformRequest(`/blog-comments${buildQuery(params)}`, "GET", token);
export const approveDocComment = (token, id) => platformRequest(`/blog-comments/${id}/approve`, "POST", token);
export const deleteDocComment = (token, id) => platformRequest(`/blog-comments/${id}`, "DELETE", token);

// --- Blog article writing (BlogArticles.js, gated by "blogarticle_manage"
// — see internal/handlers/blog_article_handler.go). Named "/blog-articles"
// (never nested under "/docs-admin") on the backend, same "its own :id
// routes can never collide with the public docs routes" reasoning
// getDocCommentsAdmin's own comment above already gives. uploadBlogArticleImage
// is a multipart call (platformRequest already passes a FormData body
// straight through untouched — see importMarketingContent above for the
// same pattern) since it always follows a successful create/update against
// the article's own id, exactly like a product's own image upload. ---
export const getBlogArticlesAdmin = (token) => platformRequest("/blog-articles", "GET", token);
export const getBlogArticleAdmin = (token, id) => platformRequest(`/blog-articles/${id}`, "GET", token);
export const createBlogArticle = (token, data) => platformRequest("/blog-articles", "POST", token, data);
export const updateBlogArticle = (token, id, data) => platformRequest(`/blog-articles/${id}`, "PUT", token, data);
export const deleteBlogArticle = (token, id) => platformRequest(`/blog-articles/${id}`, "DELETE", token);
export const uploadBlogArticleImage = (token, id, file) => {
  const formData = new FormData();
  formData.append("image", file);
  return platformRequest(`/blog-articles/${id}/image`, "POST", token, formData);
};

// --- Email deliverability (EmailDeliverability.js, gated by
// "emaildeliverability_manage" — see email_deliverability_handler.go). The
// SES bounce/complaint events themselves arrive through a completely
// separate, unauthenticated SNS webhook (ses_notification_handler.go) that
// nothing here calls directly — this is only the admin-facing read/config
// side: the event list (same paginated "data"+"meta" shape as
// getDemoRequests above) and the two-field "Setup" config
// (configuration_set/sns_topic_arn + the pre-built webhook_url the config
// GET also returns). ---
export const getEmailDeliverabilityEvents = (token, params) =>
  platformRequest(`/email-deliverability/events${buildQuery(params)}`, "GET", token);
export const getEmailDeliverabilityConfig = (token) => platformRequest("/email-deliverability/config", "GET", token);
export const updateEmailDeliverabilityConfig = (token, data) =>
  platformRequest("/email-deliverability/config", "PUT", token, data);

export default api;